import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  FileText, 
  Smartphone, 
  CreditCard, 
  Shield,
  Bike,
  Car,
  AlertCircle,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Zap,
  Users,
  Clock,
  DollarSign,
  Globe
} from 'lucide-react';

// Translations
const content = {
  en: {
    backToHome: "Back to Home",
    completeGuide: "COMPLETE GUIDE 2026",
    heroTitle: "How to Start",
    heroTitleHighlight: "Delivery Work",
    heroTitleEnd: "in Australia",
    heroSubtitle: "Everything you need to know about becoming a delivery rider with Uber Eats, DoorDash, and other platforms in Australia.",
    viewRequirements: "View Requirements",
    getHelpStarting: "Get Help Starting",
    forInternational: "For International Students & New Migrants",
    forInternationalDesc1: "Starting delivery work in Australia is a popular option for international students and new migrants looking for flexible income. However, the registration process can be confusing with different requirements for each platform.",
    forInternationalDesc2: "This guide explains the typical requirements, vehicle options, and common mistakes to avoid when signing up for platforms like Uber Eats and DoorDash.",
    whatYouNeed: "What You'll Need",
    whatYouNeedSubtitle: "Typical requirements for delivery platform registration in Australia",
    requirements: [
      { title: "ID Documents", desc: "Valid passport or government-issued ID for identity verification" },
      { title: "Work Rights", desc: "Visa with work rights in Australia (VEVO verification)" },
      { title: "Smartphone", desc: "Compatible smartphone to use the delivery app" },
      { title: "Bank Account", desc: "Australian bank account for receiving payments" },
    ],
    policeCheck: "Police Check",
    policeCheckDesc: "Some delivery platforms may require a Police Check during registration. This check is arranged directly through the delivery platform.",
    policeCheckExample: "Uber may charge approximately",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "This cost is not included in Just Delivery services and must be paid directly to the platform. Just Delivery provides guidance regarding the onboarding process but does not issue or process Police Checks.",
    licenceTitle: "Driver Licence Requirements",
    licenceSubtitle: "Licence requirements depend on the vehicle you use for delivery",
    carDelivery: "Car Delivery",
    carDeliveryDesc: "Platforms such as Uber Eats may allow delivery using a valid overseas driver's licence for individuals holding a temporary visa, provided all platform requirements are met.",
    carDeliveryNote: "May use overseas licence (subject to platform and state requirements)",
    scooterDelivery: "Scooter / Motorbike Delivery",
    scooterDeliveryDesc: "Scooter or motorbike delivery may require an Australian licence or conversion of your overseas licence. Requirements vary by state.",
    scooterOverseasNote: "Overseas licence holders:",
    scooterOverseasDesc: "Scooter or motorbike delivery may require conversion of an overseas licence. Contact your state's transport authority for conversion requirements.",
    scooterLicenceNote: "May require licence conversion",
    scooterLicenceLink: "See how to convert your licence",
    ebikeDelivery: "E-Bike / Bicycle Delivery",
    ebikeDeliveryDesc: "No driver's licence is typically required for bicycle or e-bike delivery, making it the easiest option to start.",
    ebikeLicenceNote: "No licence required for e-bikes under 250W",
    licenceDisclaimer: "Just Delivery provides guidance only. Riders are responsible for ensuring they meet the licence requirements of the delivery platforms.",
    vehicleOptions: "Vehicle Options",
    vehicleOptionsSubtitle: "Choose the delivery method that works best for your situation",
    vehicles: {
      ebike: { title: "E-Bike", desc: "Often the easiest way to start. Many riders begin with e-bikes.", pros: "No licence required, low cost to start", cons: "Limited range, weather dependent", req: "No licence needed" },
      car: { title: "Car", desc: "Good for longer distances and larger orders.", pros: "More comfort, can work in any weather", cons: "Fuel costs, parking challenges", req: "Valid licence (overseas may work)" },
      scooter: { title: "Scooter", desc: "Fast and efficient for food delivery in cities.", pros: "Faster than bikes, lower costs than cars", cons: "May require licence conversion", req: "Licence conversion may be required" },
    },
    licenceLabel: "Licence:",
    onboardingProcess: "The Onboarding Process",
    onboardingSubtitle: "Step-by-step flow from application to your first delivery",
    steps: [
      { title: "Prepare Documents", desc: "Gather your ID, visa documents, and bank details before starting the application." },
      { title: "Choose Your Platform", desc: "Select Uber Eats, DoorDash, or another delivery platform based on your preferences." },
      { title: "Submit Application", desc: "Complete the online application and upload required documents for verification." },
      { title: "Background Check", desc: "Some platforms may require a Police Check (additional fee applies)." },
      { title: "Get Approved", desc: "Once approved, download the app and complete any required training." },
      { title: "Start Earning", desc: "Go online when you're ready and start accepting delivery requests!" },
    ],
    commonMistakes: "Common Mistakes to Avoid",
    commonMistakesSubtitle: "Don't let these common errors delay your application",
    mistakes: [
      { title: "Submitting Incorrect Documents", desc: "Using expired documents or submitting the wrong type of ID can cause delays or rejection." },
      { title: "Misunderstanding Licence Requirements", desc: "Applying for scooter delivery without checking licence conversion requirements may delay your application." },
      { title: "Choosing the Wrong Vehicle Option", desc: "Selecting a vehicle type you don't have the proper licence for will delay your application." },
      { title: "Not Having Work Rights", desc: "Attempting to register without valid work rights in Australia will result in rejection." },
    ],
    howWeHelp: "How Just Delivery Helps",
    howWeHelpDesc: "Just Delivery helps riders prepare their documents and understand the onboarding process. We review your eligibility, guide you through requirements, and help you avoid common mistakes that could delay your application.",
    helpFeatures: [
      { title: "Document Review", desc: "We check your documents before you apply" },
      { title: "Expert Guidance", desc: "Step-by-step support through the process" },
      { title: "Save Time", desc: "Avoid delays from incorrect applications" },
    ],
    startApplication: "Start Your Application",
    viewPackages: "View Packages",
    disclaimer: "Just Delivery is not affiliated with Uber Eats, DoorDash, or any delivery platform. Platform approval decisions are made solely by the respective platforms.",
    serviceDisclaimer: "Just Delivery is an independent onboarding assistance service and is not affiliated with Uber Eats, DoorDash, or any delivery platform.",
    industryUpdate: {
      title: "Industry Update",
      text: "Australia is introducing new minimum pay protections and standards for food delivery riders. These reforms aim to improve pay transparency and working conditions across delivery platforms.",
    },
    typicalEarnings: {
      title: "Typical Delivery Earnings in Australia",
      ebike: { vehicle: "E-Bike", range: "$18 – $28 per hour" },
      scooter: { vehicle: "Scooter / Motorbike", range: "$22 – $35 per hour" },
      car: { vehicle: "Car", range: "$25 – $40 per hour" },
      note: "Earnings vary depending on city, demand, hours worked, and rider experience.",
    },
    ebikeRegulation: "Electric bikes used for delivery must be pedal-assist only (no throttle) to comply with Australian regulations.",
    earningsCalculator: {
      title: "Estimate Your Delivery Earnings",
      city: "City",
      cityPlaceholder: "Select city",
      cities: ["Sydney", "Melbourne", "Brisbane", "Other"],
      vehicle: "Vehicle Type",
      vehiclePlaceholder: "Select vehicle",
      vehicles: ["E-Bike", "Scooter / Motorbike", "Car"],
      hours: "Hours per Week",
      weeklyEarnings: "Estimated weekly earnings",
      monthlyEarnings: "Estimated monthly earnings",
      disclaimer: "Estimates are based on typical delivery earnings in Australia. Actual results may vary depending on city, demand, platform, and rider experience.",
    },
  },
  it: {
    backToHome: "Torna alla Home",
    completeGuide: "GUIDA COMPLETA 2026",
    heroTitle: "Come Iniziare a",
    heroTitleHighlight: "Lavorare nelle Consegne",
    heroTitleEnd: "in Australia",
    heroSubtitle: "Tutto quello che devi sapere per diventare un rider con Uber Eats, DoorDash e altre piattaforme in Australia.",
    viewRequirements: "Vedi Requisiti",
    getHelpStarting: "Ottieni Aiuto",
    forInternational: "Per Studenti Internazionali e Nuovi Migranti",
    forInternationalDesc1: "Iniziare a lavorare nelle consegne in Australia è un'opzione popolare per studenti internazionali e nuovi migranti che cercano un reddito flessibile. Tuttavia, il processo di registrazione può essere confuso con requisiti diversi per ogni piattaforma.",
    forInternationalDesc2: "Questa guida spiega i requisiti tipici, le opzioni di veicolo e gli errori comuni da evitare quando ci si iscrive a piattaforme come Uber Eats e DoorDash.",
    whatYouNeed: "Cosa Ti Serve",
    whatYouNeedSubtitle: "Requisiti tipici per la registrazione alle piattaforme di consegna in Australia",
    requirements: [
      { title: "Documenti ID", desc: "Passaporto valido o documento d'identità per la verifica" },
      { title: "Diritti di Lavoro", desc: "Visto con diritti di lavoro in Australia (verifica VEVO)" },
      { title: "Smartphone", desc: "Smartphone compatibile per usare l'app di consegna" },
      { title: "Conto Bancario", desc: "Conto bancario australiano per ricevere i pagamenti" },
    ],
    policeCheck: "Controllo di Polizia",
    policeCheckDesc: "Alcune piattaforme di consegna potrebbero richiedere un controllo di polizia durante la registrazione. Questo controllo viene organizzato direttamente tramite la piattaforma.",
    policeCheckExample: "Uber potrebbe addebitare circa",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Questo costo non è incluso nei servizi Just Delivery e deve essere pagato direttamente alla piattaforma. Just Delivery fornisce consulenza sul processo di onboarding ma non emette né elabora controlli di polizia.",
    licenceTitle: "Requisiti della Patente",
    licenceSubtitle: "I requisiti della patente dipendono dal veicolo che usi per le consegne",
    carDelivery: "Consegna con Auto",
    carDeliveryDesc: "Piattaforme come Uber Eats potrebbero consentire la consegna con una patente straniera valida per chi ha un visto temporaneo, a condizione che siano soddisfatti tutti i requisiti della piattaforma.",
    carDeliveryNote: "Può usare patente estera (soggetto a requisiti della piattaforma e dello stato)",
    scooterDelivery: "Consegna con Scooter / Moto",
    scooterDeliveryDesc: "La consegna con scooter o moto potrebbe richiedere una patente australiana o la conversione della patente estera. I requisiti variano per stato.",
    scooterOverseasNote: "Titolari di patente estera:",
    scooterOverseasDesc: "La consegna con scooter o moto potrebbe richiedere la conversione della patente estera. Contatta l'autorità dei trasporti del tuo stato per i requisiti di conversione.",
    scooterLicenceNote: "Potrebbe richiedere conversione patente",
    scooterLicenceLink: "Vedi come convertire la patente",
    ebikeDelivery: "Consegna con E-Bike / Bicicletta",
    ebikeDeliveryDesc: "Generalmente non è richiesta la patente per la consegna con bicicletta o e-bike, rendendola l'opzione più facile per iniziare.",
    ebikeLicenceNote: "Nessuna patente richiesta per e-bike sotto 250W",
    licenceDisclaimer: "Just Delivery fornisce solo consulenza. I rider sono responsabili di verificare di soddisfare i requisiti di patente delle piattaforme di consegna.",
    vehicleOptions: "Opzioni Veicolo",
    vehicleOptionsSubtitle: "Scegli il metodo di consegna più adatto alla tua situazione",
    vehicles: {
      ebike: { title: "E-Bike", desc: "Spesso il modo più facile per iniziare. Molti rider iniziano con e-bike.", pros: "Nessuna patente richiesta, basso costo iniziale", cons: "Autonomia limitata, dipende dal meteo", req: "Nessuna patente necessaria" },
      car: { title: "Auto", desc: "Buona per distanze maggiori e ordini più grandi.", pros: "Più comfort, puoi lavorare con qualsiasi meteo", cons: "Costi carburante, difficoltà di parcheggio", req: "Patente valida (estera può funzionare)" },
      scooter: { title: "Scooter", desc: "Veloce ed efficiente per consegne in città.", pros: "Più veloce delle bici, costi inferiori alle auto", cons: "Potrebbe richiedere conversione patente", req: "Potrebbe richiedere conversione patente" },
    },
    licenceLabel: "Patente:",
    onboardingProcess: "Il Processo di Onboarding",
    onboardingSubtitle: "Passo dopo passo dall'applicazione alla tua prima consegna",
    steps: [
      { title: "Prepara i Documenti", desc: "Raccogli i tuoi documenti ID, visto e dettagli bancari prima di iniziare l'applicazione." },
      { title: "Scegli la Piattaforma", desc: "Seleziona Uber Eats, DoorDash o un'altra piattaforma in base alle tue preferenze." },
      { title: "Invia l'Applicazione", desc: "Completa l'applicazione online e carica i documenti richiesti per la verifica." },
      { title: "Controllo Background", desc: "Alcune piattaforme potrebbero richiedere un controllo di polizia (costo aggiuntivo)." },
      { title: "Ottieni l'Approvazione", desc: "Una volta approvato, scarica l'app e completa la formazione richiesta." },
      { title: "Inizia a Guadagnare", desc: "Vai online quando sei pronto e inizia ad accettare richieste di consegna!" },
    ],
    commonMistakes: "Errori Comuni da Evitare",
    commonMistakesSubtitle: "Non lasciare che questi errori ritardino la tua applicazione",
    mistakes: [
      { title: "Invio di Documenti Errati", desc: "Usare documenti scaduti o inviare il tipo sbagliato di ID può causare ritardi o rifiuto." },
      { title: "Fraintendere i Requisiti della Patente", desc: "Richiedere la consegna con scooter senza verificare i requisiti di conversione patente potrebbe ritardare la tua applicazione." },
      { title: "Scegliere l'Opzione Veicolo Sbagliata", desc: "Selezionare un tipo di veicolo per cui non hai la patente adeguata ritarderà la tua applicazione." },
      { title: "Non Avere Diritti di Lavoro", desc: "Tentare di registrarsi senza diritti di lavoro validi in Australia comporterà il rifiuto." },
    ],
    howWeHelp: "Come Just Delivery Ti Aiuta",
    howWeHelpDesc: "Just Delivery aiuta i rider a preparare i documenti e capire il processo di onboarding. Verifichiamo la tua idoneità, ti guidiamo attraverso i requisiti e ti aiutiamo a evitare errori comuni che potrebbero ritardare la tua applicazione.",
    helpFeatures: [
      { title: "Revisione Documenti", desc: "Controlliamo i tuoi documenti prima che tu faccia domanda" },
      { title: "Guida Esperta", desc: "Supporto passo dopo passo durante il processo" },
      { title: "Risparmia Tempo", desc: "Evita ritardi da applicazioni errate" },
    ],
    startApplication: "Inizia la Tua Applicazione",
    viewPackages: "Vedi Pacchetti",
    disclaimer: "Just Delivery non è affiliato con Uber Eats, DoorDash o qualsiasi piattaforma di consegna. Le decisioni di approvazione sono prese esclusivamente dalle rispettive piattaforme.",
    serviceDisclaimer: "Just Delivery è un servizio di assistenza all'onboarding indipendente e non è affiliato con Uber Eats, DoorDash o qualsiasi piattaforma di consegna.",
    industryUpdate: {
      title: "Aggiornamento del Settore",
      text: "L'Australia sta introducendo nuove protezioni salariali minime e standard per i rider di consegna cibo. Queste riforme mirano a migliorare la trasparenza retributiva e le condizioni di lavoro sulle piattaforme di consegna.",
    },
    typicalEarnings: {
      title: "Guadagni Tipici delle Consegne in Australia",
      ebike: { vehicle: "E-Bike", range: "$18 – $28 all'ora" },
      scooter: { vehicle: "Scooter / Moto", range: "$22 – $35 all'ora" },
      car: { vehicle: "Auto", range: "$25 – $40 all'ora" },
      note: "I guadagni variano in base a città, domanda, ore lavorate ed esperienza del rider.",
    },
    ebikeRegulation: "Le e-bike utilizzate per le consegne devono essere solo a pedalata assistita (senza acceleratore) per rispettare le normative australiane.",
    earningsCalculator: {
      title: "Stima i Tuoi Guadagni di Consegna",
      city: "Città",
      cityPlaceholder: "Seleziona città",
      cities: ["Sydney", "Melbourne", "Brisbane", "Altro"],
      vehicle: "Tipo di Veicolo",
      vehiclePlaceholder: "Seleziona veicolo",
      vehicles: ["E-Bike", "Scooter / Moto", "Auto"],
      hours: "Ore alla Settimana",
      weeklyEarnings: "Guadagni settimanali stimati",
      monthlyEarnings: "Guadagni mensili stimati",
      disclaimer: "Le stime si basano sui guadagni tipici delle consegne in Australia. I risultati effettivi possono variare in base a città, domanda, piattaforma ed esperienza del rider.",
    },
  },
  es: {
    backToHome: "Volver al Inicio",
    completeGuide: "GUÍA COMPLETA 2026",
    heroTitle: "Cómo Empezar a",
    heroTitleHighlight: "Trabajar en Entregas",
    heroTitleEnd: "en Australia",
    heroSubtitle: "Todo lo que necesitas saber para convertirte en repartidor con Uber Eats, DoorDash y otras plataformas en Australia.",
    viewRequirements: "Ver Requisitos",
    getHelpStarting: "Obtener Ayuda",
    forInternational: "Para Estudiantes Internacionales y Nuevos Migrantes",
    forInternationalDesc1: "Comenzar a trabajar en entregas en Australia es una opción popular para estudiantes internacionales y nuevos migrantes que buscan ingresos flexibles. Sin embargo, el proceso de registro puede ser confuso con diferentes requisitos para cada plataforma.",
    forInternationalDesc2: "Esta guía explica los requisitos típicos, opciones de vehículos y errores comunes a evitar al registrarse en plataformas como Uber Eats y DoorDash.",
    whatYouNeed: "Lo Que Necesitas",
    whatYouNeedSubtitle: "Requisitos típicos para el registro en plataformas de entrega en Australia",
    requirements: [
      { title: "Documentos de ID", desc: "Pasaporte válido o documento de identidad para verificación" },
      { title: "Derechos de Trabajo", desc: "Visa con derechos de trabajo en Australia (verificación VEVO)" },
      { title: "Smartphone", desc: "Smartphone compatible para usar la app de entregas" },
      { title: "Cuenta Bancaria", desc: "Cuenta bancaria australiana para recibir pagos" },
    ],
    policeCheck: "Control Policial",
    policeCheckDesc: "Algunas plataformas pueden requerir un control policial durante el registro. Este control se gestiona directamente a través de la plataforma.",
    policeCheckExample: "Uber puede cobrar aproximadamente",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Este costo no está incluido en los servicios de Just Delivery y debe pagarse directamente a la plataforma.",
    licenceTitle: "Requisitos de Licencia",
    licenceSubtitle: "Los requisitos de licencia dependen del vehículo que uses para entregas",
    carDelivery: "Entrega con Auto",
    carDeliveryDesc: "Plataformas como Uber Eats pueden permitir entregas con licencia extranjera válida para personas con visa temporal.",
    carDeliveryNote: "Puede usar licencia extranjera (sujeto a requisitos)",
    scooterDelivery: "Entrega con Scooter / Moto",
    scooterDeliveryDesc: "La entrega con scooter o moto puede requerir licencia australiana o conversión de licencia extranjera. Los requisitos varían por estado.",
    scooterOverseasNote: "Titulares de licencia extranjera:",
    scooterOverseasDesc: "La entrega con scooter o moto puede requerir conversión de licencia extranjera.",
    scooterLicenceNote: "Puede requerir conversión de licencia",
    scooterLicenceLink: "Ver cómo convertir tu licencia",
    ebikeDelivery: "Entrega con E-Bike / Bicicleta",
    ebikeDeliveryDesc: "Generalmente no se requiere licencia para entregas en bicicleta o e-bike.",
    ebikeLicenceNote: "No se requiere licencia para e-bikes bajo 250W",
    licenceDisclaimer: "Just Delivery solo proporciona orientación. Los repartidores son responsables de cumplir los requisitos de licencia.",
    vehicleOptions: "Opciones de Vehículo",
    vehicleOptionsSubtitle: "Elige el método de entrega que mejor se adapte a tu situación",
    vehicles: {
      ebike: { title: "E-Bike", desc: "A menudo la forma más fácil de empezar.", pros: "Sin licencia, bajo costo inicial", cons: "Alcance limitado, depende del clima", req: "Sin licencia necesaria" },
      car: { title: "Auto", desc: "Bueno para distancias largas y pedidos grandes.", pros: "Más comodidad, funciona en cualquier clima", cons: "Costos de combustible, problemas de estacionamiento", req: "Licencia válida (extranjera puede funcionar)" },
      scooter: { title: "Scooter", desc: "Rápido y eficiente para entregas en ciudad.", pros: "Más rápido que bicicletas, menos costos que autos", cons: "Puede requerir conversión de licencia", req: "Puede requerir conversión de licencia" },
    },
    licenceLabel: "Licencia:",
    onboardingProcess: "El Proceso de Registro",
    onboardingSubtitle: "Paso a paso desde la solicitud hasta tu primera entrega",
    steps: [
      { title: "Prepara Documentos", desc: "Reúne tus documentos de ID, visa y datos bancarios." },
      { title: "Elige tu Plataforma", desc: "Selecciona Uber Eats, DoorDash u otra plataforma." },
      { title: "Envía la Solicitud", desc: "Completa la solicitud online y sube los documentos." },
      { title: "Control de Antecedentes", desc: "Algunas plataformas pueden requerir control policial." },
      { title: "Obtén Aprobación", desc: "Una vez aprobado, descarga la app y completa la capacitación." },
      { title: "Comienza a Ganar", desc: "¡Conéctate cuando estés listo y empieza a aceptar entregas!" },
    ],
    commonMistakes: "Errores Comunes a Evitar",
    commonMistakesSubtitle: "No dejes que estos errores retrasen tu solicitud",
    mistakes: [
      { title: "Enviar Documentos Incorrectos", desc: "Usar documentos vencidos puede causar rechazo." },
      { title: "Malinterpretar Requisitos de Licencia", desc: "Solicitar entrega con scooter sin verificar requisitos de conversión puede retrasar tu solicitud." },
      { title: "Elegir la Opción de Vehículo Equivocada", desc: "Seleccionar un vehículo sin la licencia adecuada retrasará tu solicitud." },
      { title: "No Tener Derechos de Trabajo", desc: "Intentar registrarse sin derechos de trabajo válidos resultará en rechazo." },
    ],
    howWeHelp: "Cómo Just Delivery Te Ayuda",
    howWeHelpDesc: "Just Delivery ayuda a los repartidores a preparar documentos y entender el proceso. Verificamos tu elegibilidad y te guiamos para evitar errores.",
    helpFeatures: [
      { title: "Revisión de Documentos", desc: "Revisamos tus documentos antes de aplicar" },
      { title: "Guía Experta", desc: "Soporte paso a paso" },
      { title: "Ahorra Tiempo", desc: "Evita retrasos por solicitudes incorrectas" },
    ],
    startApplication: "Iniciar tu Solicitud",
    viewPackages: "Ver Paquetes",
    disclaimer: "Just Delivery no está afiliado con Uber Eats, DoorDash o cualquier plataforma de entrega.",
    serviceDisclaimer: "Just Delivery es un servicio de asistencia de incorporación independiente y no está afiliado con Uber Eats, DoorDash ni ninguna plataforma de entrega.",
    industryUpdate: {
      title: "Actualización del Sector",
      text: "Australia está introduciendo nuevas protecciones salariales mínimas y estándares para los repartidores de comida. Estas reformas buscan mejorar la transparencia salarial y las condiciones laborales en las plataformas de entrega.",
    },
    typicalEarnings: {
      title: "Ganancias Típicas de Entrega en Australia",
      ebike: { vehicle: "E-Bike", range: "$18 – $28 por hora" },
      scooter: { vehicle: "Scooter / Moto", range: "$22 – $35 por hora" },
      car: { vehicle: "Auto", range: "$25 – $40 por hora" },
      note: "Las ganancias varían según la ciudad, demanda, horas trabajadas y experiencia del repartidor.",
    },
    ebikeRegulation: "Las bicicletas eléctricas utilizadas para entregas deben ser solo de asistencia al pedaleo (sin acelerador) para cumplir con las regulaciones australianas.",
    earningsCalculator: {
      title: "Estima Tus Ganancias de Entrega",
      city: "Ciudad",
      cityPlaceholder: "Selecciona ciudad",
      cities: ["Sydney", "Melbourne", "Brisbane", "Otro"],
      vehicle: "Tipo de Vehículo",
      vehiclePlaceholder: "Selecciona vehículo",
      vehicles: ["E-Bike", "Scooter / Moto", "Auto"],
      hours: "Horas por Semana",
      weeklyEarnings: "Ganancias semanales estimadas",
      monthlyEarnings: "Ganancias mensuales estimadas",
      disclaimer: "Las estimaciones se basan en ganancias típicas de entrega en Australia. Los resultados reales pueden variar según la ciudad, demanda, plataforma y experiencia del repartidor.",
    },
  },
  fr: {
    backToHome: "Retour à l'Accueil",
    completeGuide: "GUIDE COMPLET 2026",
    heroTitle: "Comment Commencer à",
    heroTitleHighlight: "Travailler dans la Livraison",
    heroTitleEnd: "en Australie",
    heroSubtitle: "Tout ce que vous devez savoir pour devenir livreur avec Uber Eats, DoorDash et d'autres plateformes en Australie.",
    viewRequirements: "Voir les Exigences",
    getHelpStarting: "Obtenir de l'Aide",
    forInternational: "Pour les Étudiants Internationaux et Nouveaux Migrants",
    forInternationalDesc1: "Commencer à travailler dans la livraison en Australie est une option populaire pour les étudiants internationaux et nouveaux migrants cherchant un revenu flexible.",
    forInternationalDesc2: "Ce guide explique les exigences typiques, les options de véhicule et les erreurs courantes à éviter.",
    whatYouNeed: "Ce Dont Vous Avez Besoin",
    whatYouNeedSubtitle: "Exigences typiques pour l'inscription aux plateformes de livraison en Australie",
    requirements: [
      { title: "Documents d'Identité", desc: "Passeport valide ou pièce d'identité pour vérification" },
      { title: "Droits de Travail", desc: "Visa avec droits de travail en Australie (vérification VEVO)" },
      { title: "Smartphone", desc: "Smartphone compatible pour utiliser l'application" },
      { title: "Compte Bancaire", desc: "Compte bancaire australien pour recevoir les paiements" },
    ],
    policeCheck: "Vérification Policière",
    policeCheckDesc: "Certaines plateformes peuvent exiger une vérification policière lors de l'inscription.",
    policeCheckExample: "Uber peut facturer environ",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Ce coût n'est pas inclus dans les services Just Delivery.",
    licenceTitle: "Exigences de Permis de Conduire",
    licenceSubtitle: "Les exigences dépendent du véhicule que vous utilisez",
    carDelivery: "Livraison en Voiture",
    carDeliveryDesc: "Les plateformes comme Uber Eats peuvent autoriser la livraison avec un permis étranger valide.",
    carDeliveryNote: "Peut utiliser un permis étranger (sous réserve des exigences)",
    scooterDelivery: "Livraison en Scooter / Moto",
    scooterDeliveryDesc: "La livraison en scooter ou moto peut nécessiter un permis australien ou la conversion du permis étranger. Les exigences varient selon l'état.",
    scooterOverseasNote: "Titulaires de permis étranger:",
    scooterOverseasDesc: "La livraison en scooter peut nécessiter la conversion du permis étranger.",
    scooterLicenceNote: "Peut nécessiter une conversion de permis",
    scooterLicenceLink: "Voir comment convertir votre permis",
    ebikeDelivery: "Livraison en Vélo / E-Bike",
    ebikeDeliveryDesc: "Aucun permis n'est généralement requis pour la livraison à vélo ou e-bike.",
    ebikeLicenceNote: "Aucun permis requis pour e-bikes sous 250W",
    licenceDisclaimer: "Just Delivery fournit uniquement des conseils. Les livreurs sont responsables de respecter les exigences.",
    vehicleOptions: "Options de Véhicule",
    vehicleOptionsSubtitle: "Choisissez la méthode de livraison qui vous convient",
    vehicles: {
      ebike: { title: "E-Bike", desc: "Souvent le moyen le plus facile de commencer.", pros: "Sans permis, faible coût", cons: "Portée limitée, dépend du temps", req: "Aucun permis nécessaire" },
      car: { title: "Voiture", desc: "Bon pour les longues distances.", pros: "Plus de confort, fonctionne par tout temps", cons: "Coûts de carburant, problèmes de stationnement", req: "Permis valide (étranger peut fonctionner)" },
      scooter: { title: "Scooter", desc: "Rapide et efficace pour les livraisons en ville.", pros: "Plus rapide que les vélos", cons: "Peut nécessiter conversion de permis", req: "Peut nécessiter conversion de permis" },
    },
    licenceLabel: "Permis:",
    onboardingProcess: "Le Processus d'Inscription",
    onboardingSubtitle: "Étape par étape de la demande à votre première livraison",
    steps: [
      { title: "Préparez les Documents", desc: "Rassemblez vos documents d'identité, visa et coordonnées bancaires." },
      { title: "Choisissez Votre Plateforme", desc: "Sélectionnez Uber Eats, DoorDash ou une autre plateforme." },
      { title: "Soumettez la Demande", desc: "Complétez la demande en ligne et téléchargez les documents." },
      { title: "Vérification des Antécédents", desc: "Certaines plateformes peuvent exiger une vérification policière." },
      { title: "Obtenez l'Approbation", desc: "Une fois approuvé, téléchargez l'app et complétez la formation." },
      { title: "Commencez à Gagner", desc: "Connectez-vous quand vous êtes prêt et acceptez des livraisons!" },
    ],
    commonMistakes: "Erreurs Courantes à Éviter",
    commonMistakesSubtitle: "Ne laissez pas ces erreurs retarder votre demande",
    mistakes: [
      { title: "Soumettre des Documents Incorrects", desc: "Utiliser des documents expirés peut causer un rejet." },
      { title: "Mal Comprendre les Exigences de Permis", desc: "Demander la livraison en scooter sans vérifier les exigences de conversion peut retarder votre demande." },
      { title: "Choisir la Mauvaise Option de Véhicule", desc: "Sélectionner un véhicule sans le bon permis retardera votre demande." },
      { title: "Ne Pas Avoir de Droits de Travail", desc: "Tenter de s'inscrire sans droits de travail valides entraînera un rejet." },
    ],
    howWeHelp: "Comment Just Delivery Vous Aide",
    howWeHelpDesc: "Just Delivery aide les livreurs à préparer leurs documents et comprendre le processus.",
    helpFeatures: [
      { title: "Révision des Documents", desc: "Nous vérifions vos documents avant de postuler" },
      { title: "Guide Expert", desc: "Support étape par étape" },
      { title: "Gagnez du Temps", desc: "Évitez les retards" },
    ],
    startApplication: "Commencer Votre Demande",
    viewPackages: "Voir les Forfaits",
    disclaimer: "Just Delivery n'est pas affilié à Uber Eats, DoorDash ou toute plateforme de livraison.",
    serviceDisclaimer: "Just Delivery est un service d'assistance à l'intégration indépendant et n'est affilié à Uber Eats, DoorDash ou toute plateforme de livraison.",
    industryUpdate: {
      title: "Mise à Jour du Secteur",
      text: "L'Australie introduit de nouvelles protections salariales minimales et normes pour les livreurs de nourriture. Ces réformes visent à améliorer la transparence salariale et les conditions de travail sur les plateformes de livraison.",
    },
    typicalEarnings: {
      title: "Revenus Typiques de Livraison en Australie",
      ebike: { vehicle: "Vélo Électrique", range: "18 $ – 28 $ par heure" },
      scooter: { vehicle: "Scooter / Moto", range: "22 $ – 35 $ par heure" },
      car: { vehicle: "Voiture", range: "25 $ – 40 $ par heure" },
      note: "Les revenus varient selon la ville, la demande, les heures travaillées et l'expérience du livreur.",
    },
    ebikeRegulation: "Les vélos électriques utilisés pour la livraison doivent être uniquement à assistance au pédalage (sans accélérateur) pour respecter les réglementations australiennes.",
    earningsCalculator: {
      title: "Estimez Vos Revenus de Livraison",
      city: "Ville",
      cityPlaceholder: "Sélectionnez la ville",
      cities: ["Sydney", "Melbourne", "Brisbane", "Autre"],
      vehicle: "Type de Véhicule",
      vehiclePlaceholder: "Sélectionnez le véhicule",
      vehicles: ["Vélo Électrique", "Scooter / Moto", "Voiture"],
      hours: "Heures par Semaine",
      weeklyEarnings: "Revenus hebdomadaires estimés",
      monthlyEarnings: "Revenus mensuels estimés",
      disclaimer: "Les estimations sont basées sur les revenus typiques de livraison en Australie. Les résultats réels peuvent varier selon la ville, la demande, la plateforme et l'expérience du livreur.",
    },
  },
  vi: {
    backToHome: "Về Trang Chủ",
    completeGuide: "HƯỚNG DẪN ĐẦY ĐỦ 2026",
    heroTitle: "Cách Bắt Đầu",
    heroTitleHighlight: "Công Việc Giao Hàng",
    heroTitleEnd: "tại Úc",
    heroSubtitle: "Tất cả những gì bạn cần biết để trở thành tài xế giao hàng với Uber Eats, DoorDash và các nền tảng khác tại Úc.",
    viewRequirements: "Xem Yêu Cầu",
    getHelpStarting: "Nhận Trợ Giúp",
    forInternational: "Dành cho Sinh viên Quốc tế và Người Nhập cư Mới",
    forInternationalDesc1: "Bắt đầu công việc giao hàng tại Úc là lựa chọn phổ biến cho sinh viên quốc tế và người nhập cư mới đang tìm kiếm thu nhập linh hoạt.",
    forInternationalDesc2: "Hướng dẫn này giải thích các yêu cầu điển hình, tùy chọn phương tiện và những sai lầm phổ biến cần tránh.",
    whatYouNeed: "Những Gì Bạn Cần",
    whatYouNeedSubtitle: "Yêu cầu điển hình để đăng ký nền tảng giao hàng tại Úc",
    requirements: [
      { title: "Giấy tờ Tùy thân", desc: "Hộ chiếu hợp lệ hoặc giấy tờ tùy thân để xác minh" },
      { title: "Quyền Làm việc", desc: "Visa có quyền làm việc tại Úc (xác minh VEVO)" },
      { title: "Điện thoại", desc: "Điện thoại thông minh tương thích để sử dụng ứng dụng" },
      { title: "Tài khoản Ngân hàng", desc: "Tài khoản ngân hàng Úc để nhận thanh toán" },
    ],
    policeCheck: "Kiểm tra Lý lịch",
    policeCheckDesc: "Một số nền tảng có thể yêu cầu kiểm tra lý lịch trong quá trình đăng ký.",
    policeCheckExample: "Uber có thể tính phí khoảng",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Chi phí này không bao gồm trong dịch vụ Just Delivery.",
    licenceTitle: "Yêu cầu Bằng lái",
    licenceSubtitle: "Yêu cầu bằng lái phụ thuộc vào phương tiện bạn sử dụng",
    carDelivery: "Giao hàng bằng Ô tô",
    carDeliveryDesc: "Các nền tảng như Uber Eats có thể cho phép giao hàng với bằng lái nước ngoài hợp lệ.",
    carDeliveryNote: "Có thể sử dụng bằng lái nước ngoài (tùy theo yêu cầu)",
    scooterDelivery: "Giao hàng bằng Xe tay ga / Mô tô",
    scooterDeliveryDesc: "Các nền tảng thường yêu cầu bằng lái Úc hợp lệ để giao hàng bằng xe tay ga hoặc mô tô.",
    scooterOverseasNote: "Người có bằng lái nước ngoài:",
    scooterOverseasDesc: "Giao hàng xe tay ga có thể yêu cầu chuyển đổi bằng lái nước ngoài.",
    scooterLicenceNote: "Có thể cần chuyển đổi bằng lái",
    scooterLicenceLink: "Xem cách chuyển đổi bằng lái",
    ebikeDelivery: "Giao hàng bằng E-Bike / Xe đạp",
    ebikeDeliveryDesc: "Thường không yêu cầu bằng lái để giao hàng bằng xe đạp hoặc e-bike.",
    ebikeLicenceNote: "Không cần bằng lái cho e-bike dưới 250W",
    licenceDisclaimer: "Just Delivery chỉ cung cấp hướng dẫn. Tài xế chịu trách nhiệm đảm bảo đáp ứng yêu cầu.",
    vehicleOptions: "Tùy chọn Phương tiện",
    vehicleOptionsSubtitle: "Chọn phương thức giao hàng phù hợp nhất với bạn",
    vehicles: {
      ebike: { title: "E-Bike", desc: "Thường là cách dễ nhất để bắt đầu.", pros: "Không cần bằng lái, chi phí thấp", cons: "Phạm vi hạn chế, phụ thuộc thời tiết", req: "Không cần bằng lái" },
      car: { title: "Ô tô", desc: "Tốt cho khoảng cách xa và đơn hàng lớn.", pros: "Thoải mái hơn, làm việc mọi thời tiết", cons: "Chi phí nhiên liệu, khó đỗ xe", req: "Bằng lái hợp lệ (nước ngoài có thể được)" },
      scooter: { title: "Xe tay ga", desc: "Nhanh và hiệu quả cho giao hàng trong thành phố.", pros: "Nhanh hơn xe đạp, chi phí thấp hơn ô tô", cons: "Có thể cần chuyển đổi bằng lái", req: "Có thể cần chuyển đổi bằng lái" },
    },
    licenceLabel: "Bằng lái:",
    onboardingProcess: "Quy trình Đăng ký",
    onboardingSubtitle: "Từng bước từ đăng ký đến chuyến giao hàng đầu tiên",
    steps: [
      { title: "Chuẩn bị Giấy tờ", desc: "Thu thập giấy tờ tùy thân, visa và thông tin ngân hàng." },
      { title: "Chọn Nền tảng", desc: "Chọn Uber Eats, DoorDash hoặc nền tảng khác." },
      { title: "Gửi Đăng ký", desc: "Hoàn thành đăng ký online và tải lên giấy tờ." },
      { title: "Kiểm tra Lý lịch", desc: "Một số nền tảng có thể yêu cầu kiểm tra lý lịch." },
      { title: "Được Chấp thuận", desc: "Sau khi được duyệt, tải ứng dụng và hoàn thành đào tạo." },
      { title: "Bắt đầu Kiếm tiền", desc: "Kết nối khi sẵn sàng và bắt đầu nhận đơn giao hàng!" },
    ],
    commonMistakes: "Những Sai lầm Phổ biến Cần Tránh",
    commonMistakesSubtitle: "Đừng để những lỗi này làm chậm đăng ký của bạn",
    mistakes: [
      { title: "Gửi Giấy tờ Sai", desc: "Sử dụng giấy tờ hết hạn có thể gây từ chối." },
      { title: "Hiểu Sai Yêu cầu Bằng lái", desc: "Đăng ký giao hàng xe tay ga không có bằng Úc sẽ bị từ chối." },
      { title: "Chọn Sai Tùy chọn Phương tiện", desc: "Chọn loại xe không có bằng lái phù hợp sẽ làm chậm đăng ký." },
      { title: "Không Có Quyền Làm việc", desc: "Cố gắng đăng ký mà không có quyền làm việc sẽ bị từ chối." },
    ],
    howWeHelp: "Just Delivery Giúp Bạn Như Thế Nào",
    howWeHelpDesc: "Just Delivery giúp tài xế chuẩn bị giấy tờ và hiểu quy trình.",
    helpFeatures: [
      { title: "Kiểm tra Giấy tờ", desc: "Chúng tôi kiểm tra giấy tờ trước khi bạn đăng ký" },
      { title: "Hướng dẫn Chuyên gia", desc: "Hỗ trợ từng bước" },
      { title: "Tiết kiệm Thời gian", desc: "Tránh chậm trễ từ đăng ký sai" },
    ],
    startApplication: "Bắt đầu Đăng ký",
    viewPackages: "Xem Gói dịch vụ",
    disclaimer: "Just Delivery không liên kết với Uber Eats, DoorDash hoặc bất kỳ nền tảng giao hàng nào.",
    serviceDisclaimer: "Just Delivery là dịch vụ hỗ trợ đăng ký độc lập và không liên kết với Uber Eats, DoorDash hoặc bất kỳ nền tảng giao hàng nào.",
    industryUpdate: {
      title: "Cập Nhật Ngành",
      text: "Úc đang giới thiệu các quy định bảo vệ mức lương tối thiểu và tiêu chuẩn mới cho tài xế giao hàng thực phẩm. Những cải cách này nhằm cải thiện tính minh bạch về lương và điều kiện làm việc trên các nền tảng giao hàng.",
    },
    typicalEarnings: {
      title: "Thu Nhập Giao Hàng Điển Hình tại Úc",
      ebike: { vehicle: "E-Bike", range: "$18 – $28 mỗi giờ" },
      scooter: { vehicle: "Xe Tay Ga / Xe Máy", range: "$22 – $35 mỗi giờ" },
      car: { vehicle: "Ô Tô", range: "$25 – $40 mỗi giờ" },
      note: "Thu nhập thay đổi tùy theo thành phố, nhu cầu, giờ làm việc và kinh nghiệm của tài xế.",
    },
    ebikeRegulation: "Xe đạp điện dùng để giao hàng phải chỉ là loại hỗ trợ đạp (không có ga) để tuân thủ quy định của Úc.",
    earningsCalculator: {
      title: "Ước Tính Thu Nhập Giao Hàng của Bạn",
      city: "Thành phố",
      cityPlaceholder: "Chọn thành phố",
      cities: ["Sydney", "Melbourne", "Brisbane", "Khác"],
      vehicle: "Loại Phương Tiện",
      vehiclePlaceholder: "Chọn phương tiện",
      vehicles: ["E-Bike", "Xe Tay Ga / Xe Máy", "Ô Tô"],
      hours: "Giờ mỗi Tuần",
      weeklyEarnings: "Thu nhập hàng tuần ước tính",
      monthlyEarnings: "Thu nhập hàng tháng ước tính",
      disclaimer: "Ước tính dựa trên thu nhập giao hàng điển hình tại Úc. Kết quả thực tế có thể thay đổi tùy theo thành phố, nhu cầu, nền tảng và kinh nghiệm của tài xế.",
    },
  },
  tl: {
    backToHome: "Bumalik sa Home",
    completeGuide: "KUMPLETONG GABAY 2026",
    heroTitle: "Paano Magsimula ng",
    heroTitleHighlight: "Trabaho sa Delivery",
    heroTitleEnd: "sa Australia",
    heroSubtitle: "Lahat ng kailangan mong malaman para maging delivery rider sa Uber Eats, DoorDash at ibang platform sa Australia.",
    viewRequirements: "Tingnan ang Requirements",
    getHelpStarting: "Kumuha ng Tulong",
    forInternational: "Para sa International Students at Bagong Migrants",
    forInternationalDesc1: "Ang pagtatrabaho sa delivery sa Australia ay popular na opsyon para sa international students at bagong migrants na naghahanap ng flexible na kita.",
    forInternationalDesc2: "Ipinapaliwanag ng gabay na ito ang mga karaniwang kinakailangan, mga opsyon sa sasakyan at mga karaniwang pagkakamali.",
    whatYouNeed: "Ano ang Kailangan Mo",
    whatYouNeedSubtitle: "Karaniwang requirements para sa registration sa delivery platforms sa Australia",
    requirements: [
      { title: "ID Documents", desc: "Valid na passport o government ID para sa verification" },
      { title: "Work Rights", desc: "Visa na may work rights sa Australia (VEVO verification)" },
      { title: "Smartphone", desc: "Compatible na smartphone para gamitin ang delivery app" },
      { title: "Bank Account", desc: "Australian bank account para sa pagbabayad" },
    ],
    policeCheck: "Police Check",
    policeCheckDesc: "Ang ilang platform ay maaaring mangailangan ng Police Check sa registration.",
    policeCheckExample: "Ang Uber ay maaaring magcharge ng humigit-kumulang",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Hindi kasama ang gastos na ito sa mga serbisyo ng Just Delivery.",
    licenceTitle: "Mga Kinakailangan sa Driver's Licence",
    licenceSubtitle: "Ang mga kinakailangan ay depende sa sasakyan na gagamitin mo",
    carDelivery: "Car Delivery",
    carDeliveryDesc: "Ang mga platform tulad ng Uber Eats ay maaaring payagan ang delivery gamit ang valid na overseas licence.",
    carDeliveryNote: "Maaaring gumamit ng overseas licence (depende sa requirements)",
    scooterDelivery: "Scooter / Motorbike Delivery",
    scooterDeliveryDesc: "Ang mga platform ay karaniwang nangangailangan ng valid na Australian licence para sa scooter o motorbike.",
    scooterOverseasNote: "May overseas licence:",
    scooterOverseasDesc: "Ang scooter delivery ay maaaring mangailangan ng conversion ng overseas licence.",
    scooterLicenceNote: "Maaaring mangailangan ng conversion ng licence",
    scooterLicenceLink: "Tingnan kung paano i-convert ang licence",
    ebikeDelivery: "E-Bike / Bicycle Delivery",
    ebikeDeliveryDesc: "Karaniwang hindi kinakailangan ang licence para sa bicycle o e-bike delivery.",
    ebikeLicenceNote: "Walang licence na kailangan para sa e-bikes under 250W",
    licenceDisclaimer: "Ang Just Delivery ay nagbibigay lamang ng gabay. Responsibilidad ng mga rider na sumunod sa requirements.",
    vehicleOptions: "Mga Opsyon sa Sasakyan",
    vehicleOptionsSubtitle: "Piliin ang delivery method na pinakamabuti para sa iyo",
    vehicles: {
      ebike: { title: "E-Bike", desc: "Kadalasang pinakamadaling paraan para magsimula.", pros: "Walang licence, mababang gastos", cons: "Limitadong range, depende sa panahon", req: "Walang licence na kailangan" },
      car: { title: "Car", desc: "Mabuti para sa malayong distansya.", pros: "Mas komportable, pwede kahit anong panahon", cons: "Gastos sa gasolina, problema sa parking", req: "Valid na licence (overseas pwede)" },
      scooter: { title: "Scooter", desc: "Mabilis at efficient para sa delivery sa lungsod.", pros: "Mas mabilis kaysa bikes", cons: "Maaaring mangailangan ng conversion", req: "Maaaring mangailangan ng conversion ng licence" },
    },
    licenceLabel: "Licence:",
    onboardingProcess: "Ang Proseso ng Onboarding",
    onboardingSubtitle: "Hakbang-hakbang mula sa application hanggang sa unang delivery mo",
    steps: [
      { title: "Ihanda ang Documents", desc: "Kolektahin ang iyong ID, visa documents at bank details." },
      { title: "Pumili ng Platform", desc: "Piliin ang Uber Eats, DoorDash o ibang platform." },
      { title: "Isumite ang Application", desc: "Kumpletuhin ang online application at i-upload ang documents." },
      { title: "Background Check", desc: "Ang ilang platform ay maaaring mangailangan ng Police Check." },
      { title: "Makuha ang Approval", desc: "Kapag approved, i-download ang app at kumpletuhin ang training." },
      { title: "Magsimulang Kumita", desc: "Mag-online kapag handa ka at magsimulang tumanggap ng deliveries!" },
    ],
    commonMistakes: "Karaniwang Pagkakamali na Dapat Iwasan",
    commonMistakesSubtitle: "Huwag hayaang ma-delay ang iyong application dahil sa mga error na ito",
    mistakes: [
      { title: "Pagsusumite ng Maling Documents", desc: "Ang paggamit ng expired documents ay maaaring magdulot ng rejection." },
      { title: "Hindi Pag-unawa sa Licence Requirements", desc: "Ang pag-apply para sa scooter delivery nang walang Australian licence ay matatanggihan." },
      { title: "Pagpili ng Maling Vehicle Option", desc: "Ang pagpili ng vehicle na wala kang tamang licence ay magde-delay sa application." },
      { title: "Walang Work Rights", desc: "Ang pagtangkang mag-register nang walang valid na work rights ay matatanggihan." },
    ],
    howWeHelp: "Paano Nakakatulong ang Just Delivery",
    howWeHelpDesc: "Tinutulungan ng Just Delivery ang mga rider na maghanda ng documents at maintindihan ang proseso.",
    helpFeatures: [
      { title: "Document Review", desc: "Sinusuri namin ang iyong documents bago ka mag-apply" },
      { title: "Expert Guidance", desc: "Hakbang-hakbang na suporta" },
      { title: "Makatipid ng Oras", desc: "Iwasan ang delays" },
    ],
    startApplication: "Simulan ang Application",
    viewPackages: "Tingnan ang Packages",
    disclaimer: "Ang Just Delivery ay hindi affiliated sa Uber Eats, DoorDash o anumang delivery platform.",
    serviceDisclaimer: "Ang Just Delivery ay isang independent na onboarding assistance service at hindi affiliated sa Uber Eats, DoorDash, o anumang delivery platform.",
    industryUpdate: {
      title: "Industry Update",
      text: "Ang Australia ay nagpapakilala ng mga bagong minimum pay protections at standards para sa mga food delivery rider. Ang mga reforms na ito ay naglalayong mapabuti ang pay transparency at working conditions sa mga delivery platform.",
    },
    typicalEarnings: {
      title: "Typical na Kita sa Delivery sa Australia",
      ebike: { vehicle: "E-Bike", range: "$18 – $28 bawat oras" },
      scooter: { vehicle: "Scooter / Motorbike", range: "$22 – $35 bawat oras" },
      car: { vehicle: "Kotse", range: "$25 – $40 bawat oras" },
      note: "Ang kita ay nag-iiba depende sa lungsod, demand, oras ng trabaho at karanasan ng rider.",
    },
    ebikeRegulation: "Ang mga electric bike na ginagamit sa delivery ay dapat pedal-assist lamang (walang throttle) para sumunod sa Australian regulations.",
    earningsCalculator: {
      title: "I-estimate ang Iyong Kita sa Delivery",
      city: "Lungsod",
      cityPlaceholder: "Pumili ng lungsod",
      cities: ["Sydney", "Melbourne", "Brisbane", "Iba pa"],
      vehicle: "Uri ng Sasakyan",
      vehiclePlaceholder: "Pumili ng sasakyan",
      vehicles: ["E-Bike", "Scooter / Motorbike", "Kotse"],
      hours: "Oras bawat Linggo",
      weeklyEarnings: "Estimated lingguhang kita",
      monthlyEarnings: "Estimated buwanang kita",
      disclaimer: "Ang mga estimate ay batay sa typical na kita sa delivery sa Australia. Ang aktwal na resulta ay maaaring mag-iba depende sa lungsod, demand, platform, at karanasan ng rider.",
    },
  },
  ms: {
    backToHome: "Kembali ke Utama",
    completeGuide: "PANDUAN LENGKAP 2026",
    heroTitle: "Cara Memulakan",
    heroTitleHighlight: "Kerja Penghantaran",
    heroTitleEnd: "di Australia",
    heroSubtitle: "Semua yang anda perlu tahu untuk menjadi rider penghantaran dengan Uber Eats, DoorDash dan platform lain di Australia.",
    viewRequirements: "Lihat Keperluan",
    getHelpStarting: "Dapatkan Bantuan",
    forInternational: "Untuk Pelajar Antarabangsa dan Migran Baru",
    forInternationalDesc1: "Memulakan kerja penghantaran di Australia adalah pilihan popular untuk pelajar antarabangsa dan migran baru yang mencari pendapatan fleksibel.",
    forInternationalDesc2: "Panduan ini menerangkan keperluan tipikal, pilihan kenderaan dan kesilapan biasa yang perlu dielakkan.",
    whatYouNeed: "Apa Yang Anda Perlukan",
    whatYouNeedSubtitle: "Keperluan tipikal untuk pendaftaran platform penghantaran di Australia",
    requirements: [
      { title: "Dokumen ID", desc: "Pasport sah atau ID kerajaan untuk pengesahan" },
      { title: "Hak Bekerja", desc: "Visa dengan hak bekerja di Australia (pengesahan VEVO)" },
      { title: "Telefon Pintar", desc: "Telefon pintar yang serasi untuk menggunakan aplikasi" },
      { title: "Akaun Bank", desc: "Akaun bank Australia untuk menerima bayaran" },
    ],
    policeCheck: "Semakan Polis",
    policeCheckDesc: "Sesetengah platform mungkin memerlukan semakan polis semasa pendaftaran.",
    policeCheckExample: "Uber mungkin mengenakan caj kira-kira",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Kos ini tidak termasuk dalam perkhidmatan Just Delivery.",
    licenceTitle: "Keperluan Lesen Memandu",
    licenceSubtitle: "Keperluan lesen bergantung pada kenderaan yang anda gunakan",
    carDelivery: "Penghantaran Kereta",
    carDeliveryDesc: "Platform seperti Uber Eats mungkin membenarkan penghantaran dengan lesen asing yang sah.",
    carDeliveryNote: "Boleh menggunakan lesen asing (tertakluk kepada keperluan)",
    scooterDelivery: "Penghantaran Skuter / Motosikal",
    scooterDeliveryDesc: "Platform umumnya memerlukan lesen Australia yang sah untuk penghantaran skuter atau motosikal.",
    scooterOverseasNote: "Pemegang lesen asing:",
    scooterOverseasDesc: "Penghantaran skuter mungkin memerlukan penukaran lesen asing.",
    scooterLicenceNote: "Mungkin memerlukan penukaran lesen",
    scooterLicenceLink: "Lihat cara menukar lesen anda",
    ebikeDelivery: "Penghantaran E-Bike / Basikal",
    ebikeDeliveryDesc: "Biasanya tiada lesen diperlukan untuk penghantaran basikal atau e-bike.",
    ebikeLicenceNote: "Tiada lesen diperlukan untuk e-bike di bawah 250W",
    licenceDisclaimer: "Just Delivery hanya menyediakan panduan. Rider bertanggungjawab memenuhi keperluan.",
    vehicleOptions: "Pilihan Kenderaan",
    vehicleOptionsSubtitle: "Pilih kaedah penghantaran yang paling sesuai untuk anda",
    vehicles: {
      ebike: { title: "E-Bike", desc: "Selalunya cara paling mudah untuk bermula.", pros: "Tiada lesen, kos rendah", cons: "Jarak terhad, bergantung cuaca", req: "Tiada lesen diperlukan" },
      car: { title: "Kereta", desc: "Baik untuk jarak jauh.", pros: "Lebih selesa, boleh bekerja dalam apa-apa cuaca", cons: "Kos petrol, masalah parkir", req: "Lesen sah (asing mungkin boleh)" },
      scooter: { title: "Skuter", desc: "Cepat dan cekap untuk penghantaran di bandar.", pros: "Lebih cepat dari basikal", cons: "Mungkin perlu penukaran lesen", req: "Mungkin perlu penukaran lesen" },
    },
    licenceLabel: "Lesen:",
    onboardingProcess: "Proses Onboarding",
    onboardingSubtitle: "Langkah demi langkah dari permohonan ke penghantaran pertama anda",
    steps: [
      { title: "Sediakan Dokumen", desc: "Kumpulkan dokumen ID, visa dan butiran bank anda." },
      { title: "Pilih Platform", desc: "Pilih Uber Eats, DoorDash atau platform lain." },
      { title: "Hantar Permohonan", desc: "Lengkapkan permohonan online dan muat naik dokumen." },
      { title: "Semakan Latar Belakang", desc: "Sesetengah platform mungkin memerlukan semakan polis." },
      { title: "Dapatkan Kelulusan", desc: "Setelah diluluskan, muat turun aplikasi dan lengkapkan latihan." },
      { title: "Mula Menjana", desc: "Pergi online apabila anda bersedia dan mula terima penghantaran!" },
    ],
    commonMistakes: "Kesilapan Biasa Yang Perlu Dielakkan",
    commonMistakesSubtitle: "Jangan biarkan kesilapan ini melambatkan permohonan anda",
    mistakes: [
      { title: "Menghantar Dokumen Salah", desc: "Menggunakan dokumen tamat tempoh boleh menyebabkan penolakan." },
      { title: "Salah Faham Keperluan Lesen", desc: "Memohon penghantaran skuter tanpa lesen Australia akan ditolak." },
      { title: "Memilih Pilihan Kenderaan Salah", desc: "Memilih kenderaan tanpa lesen yang betul akan melambatkan permohonan." },
      { title: "Tiada Hak Bekerja", desc: "Cuba mendaftar tanpa hak bekerja yang sah akan ditolak." },
    ],
    howWeHelp: "Bagaimana Just Delivery Membantu",
    howWeHelpDesc: "Just Delivery membantu rider menyediakan dokumen dan memahami proses.",
    helpFeatures: [
      { title: "Semakan Dokumen", desc: "Kami semak dokumen anda sebelum anda memohon" },
      { title: "Panduan Pakar", desc: "Sokongan langkah demi langkah" },
      { title: "Jimat Masa", desc: "Elakkan kelewatan" },
    ],
    startApplication: "Mulakan Permohonan",
    viewPackages: "Lihat Pakej",
    disclaimer: "Just Delivery tidak bergabung dengan Uber Eats, DoorDash atau mana-mana platform penghantaran.",
    serviceDisclaimer: "Just Delivery adalah perkhidmatan bantuan onboarding bebas dan tidak bergabung dengan Uber Eats, DoorDash, atau mana-mana platform penghantaran.",
    industryUpdate: {
      title: "Kemas Kini Industri",
      text: "Australia sedang memperkenalkan perlindungan gaji minimum baru dan standard untuk penghantar makanan. Pembaharuan ini bertujuan untuk meningkatkan ketelusan gaji dan keadaan kerja di seluruh platform penghantaran.",
    },
    typicalEarnings: {
      title: "Pendapatan Penghantaran Tipikal di Australia",
      ebike: { vehicle: "E-Bike", range: "$18 – $28 sejam" },
      scooter: { vehicle: "Skuter / Motosikal", range: "$22 – $35 sejam" },
      car: { vehicle: "Kereta", range: "$25 – $40 sejam" },
      note: "Pendapatan berbeza bergantung pada bandar, permintaan, jam bekerja dan pengalaman penghantar.",
    },
    ebikeRegulation: "Basikal elektrik yang digunakan untuk penghantaran mestilah jenis bantuan pedal sahaja (tiada throttle) untuk mematuhi peraturan Australia.",
    earningsCalculator: {
      title: "Anggarkan Pendapatan Penghantaran Anda",
      city: "Bandar",
      cityPlaceholder: "Pilih bandar",
      cities: ["Sydney", "Melbourne", "Brisbane", "Lain-lain"],
      vehicle: "Jenis Kenderaan",
      vehiclePlaceholder: "Pilih kenderaan",
      vehicles: ["E-Bike", "Skuter / Motosikal", "Kereta"],
      hours: "Jam setiap Minggu",
      weeklyEarnings: "Anggaran pendapatan mingguan",
      monthlyEarnings: "Anggaran pendapatan bulanan",
      disclaimer: "Anggaran berdasarkan pendapatan penghantaran tipikal di Australia. Keputusan sebenar mungkin berbeza bergantung pada bandar, permintaan, platform dan pengalaman penghantar.",
    },
  },
  tr: {
    backToHome: "Ana Sayfaya Dön",
    completeGuide: "TAM KILAVUZ 2026",
    heroTitle: "Nasıl Başlanır",
    heroTitleHighlight: "Kurye İşi",
    heroTitleEnd: "Avustralya'da",
    heroSubtitle: "Uber Eats, DoorDash ve diğer platformlarda kurye olmak için bilmeniz gereken her şey.",
    viewRequirements: "Gereksinimleri Gör",
    getHelpStarting: "Yardım Al",
    forInternational: "Uluslararası Öğrenciler ve Yeni Göçmenler İçin",
    forInternationalDesc1: "Avustralya'da kurye işine başlamak, esnek gelir arayan uluslararası öğrenciler ve yeni göçmenler için popüler bir seçenek.",
    forInternationalDesc2: "Bu kılavuz tipik gereksinimleri, araç seçeneklerini ve kaçınılması gereken yaygın hataları açıklıyor.",
    whatYouNeed: "Neye İhtiyacınız Var",
    whatYouNeedSubtitle: "Avustralya'da teslimat platformu kaydı için tipik gereksinimler",
    requirements: [
      { title: "Kimlik Belgeleri", desc: "Doğrulama için geçerli pasaport veya kimlik" },
      { title: "Çalışma Hakları", desc: "Avustralya'da çalışma hakları olan vize (VEVO doğrulaması)" },
      { title: "Akıllı Telefon", desc: "Uygulamayı kullanmak için uyumlu akıllı telefon" },
      { title: "Banka Hesabı", desc: "Ödeme almak için Avustralya banka hesabı" },
    ],
    policeCheck: "Polis Kontrolü",
    policeCheckDesc: "Bazı platformlar kayıt sırasında polis kontrolü isteyebilir.",
    policeCheckExample: "Uber yaklaşık olarak ücret alabilir",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Bu maliyet Just Delivery hizmetlerine dahil değildir.",
    licenceTitle: "Ehliyet Gereksinimleri",
    licenceSubtitle: "Ehliyet gereksinimleri kullandığınız araca bağlıdır",
    carDelivery: "Araba ile Teslimat",
    carDeliveryDesc: "Uber Eats gibi platformlar geçerli yabancı ehliyetle teslimata izin verebilir.",
    carDeliveryNote: "Yabancı ehliyet kullanılabilir (gereksinimlere tabi)",
    scooterDelivery: "Scooter / Motorsiklet Teslimatı",
    scooterDeliveryDesc: "Platformlar genellikle scooter veya motorsiklet için geçerli Avustralya ehliyeti gerektirir.",
    scooterOverseasNote: "Yabancı ehliyet sahipleri:",
    scooterOverseasDesc: "Scooter teslimatı yabancı ehliyetin dönüştürülmesini gerektirebilir.",
    scooterLicenceNote: "Ehliyet dönüştürme gerekebilir",
    scooterLicenceLink: "Ehliyetinizi nasıl dönüştüreceğinizi görün",
    ebikeDelivery: "E-Bisiklet / Bisiklet Teslimatı",
    ebikeDeliveryDesc: "Bisiklet veya e-bisiklet teslimatı için genellikle ehliyet gerekmez.",
    ebikeLicenceNote: "250W altı e-bisikletler için ehliyet gerekmez",
    licenceDisclaimer: "Just Delivery sadece rehberlik sağlar. Kuryeler gereksinimleri karşılamaktan sorumludur.",
    vehicleOptions: "Araç Seçenekleri",
    vehicleOptionsSubtitle: "Sizin için en uygun teslimat yöntemini seçin",
    vehicles: {
      ebike: { title: "E-Bisiklet", desc: "Genellikle başlamanın en kolay yolu.", pros: "Ehliyet yok, düşük maliyet", cons: "Sınırlı menzil, havaya bağlı", req: "Ehliyet gerekmez" },
      car: { title: "Araba", desc: "Uzun mesafeler için iyi.", pros: "Daha rahat, her havada çalışabilir", cons: "Yakıt maliyetleri, park sorunları", req: "Geçerli ehliyet (yabancı olabilir)" },
      scooter: { title: "Scooter", desc: "Şehir içi teslimat için hızlı ve verimli.", pros: "Bisikletlerden hızlı", cons: "Ehliyet dönüştürme gerekebilir", req: "Ehliyet dönüştürme gerekebilir" },
    },
    licenceLabel: "Ehliyet:",
    onboardingProcess: "Kayıt Süreci",
    onboardingSubtitle: "Başvurudan ilk teslimatınıza adım adım",
    steps: [
      { title: "Belgeleri Hazırlayın", desc: "Kimlik, vize belgelerinizi ve banka bilgilerinizi toplayın." },
      { title: "Platformunuzu Seçin", desc: "Uber Eats, DoorDash veya başka bir platform seçin." },
      { title: "Başvuruyu Gönderin", desc: "Online başvuruyu tamamlayın ve belgeleri yükleyin." },
      { title: "Arka Plan Kontrolü", desc: "Bazı platformlar polis kontrolü isteyebilir." },
      { title: "Onay Alın", desc: "Onaylandıktan sonra uygulamayı indirin ve eğitimi tamamlayın." },
      { title: "Kazanmaya Başlayın", desc: "Hazır olduğunuzda çevrimiçi olun ve teslimat kabul etmeye başlayın!" },
    ],
    commonMistakes: "Kaçınılması Gereken Yaygın Hatalar",
    commonMistakesSubtitle: "Bu hataların başvurunuzu geciktirmesine izin vermeyin",
    mistakes: [
      { title: "Yanlış Belge Göndermek", desc: "Süresi dolmuş belge kullanmak reddedilmeye neden olabilir." },
      { title: "Ehliyet Gereksinimlerini Yanlış Anlamak", desc: "Avustralya ehliyeti olmadan scooter teslimatı başvurusu reddedilecektir." },
      { title: "Yanlış Araç Seçeneği Seçmek", desc: "Uygun ehliyet olmadan araç seçmek başvurunuzu geciktirecektir." },
      { title: "Çalışma Hakları Olmamak", desc: "Geçerli çalışma hakları olmadan kayıt olmaya çalışmak reddedilecektir." },
    ],
    howWeHelp: "Just Delivery Nasıl Yardımcı Olur",
    howWeHelpDesc: "Just Delivery kuryelerin belge hazırlamasına ve süreci anlamasına yardımcı olur.",
    helpFeatures: [
      { title: "Belge İncelemesi", desc: "Başvurmadan önce belgelerinizi kontrol ederiz" },
      { title: "Uzman Rehberliği", desc: "Adım adım destek" },
      { title: "Zaman Kazanın", desc: "Gecikmelerden kaçının" },
    ],
    startApplication: "Başvurunuzu Başlatın",
    viewPackages: "Paketleri Görüntüle",
    disclaimer: "Just Delivery, Uber Eats, DoorDash veya herhangi bir teslimat platformuyla bağlantılı değildir.",
    serviceDisclaimer: "Just Delivery bağımsız bir kayıt yardım hizmetidir ve Uber Eats, DoorDash veya herhangi bir teslimat platformuyla bağlantılı değildir.",
    industryUpdate: {
      title: "Sektör Güncellemesi",
      text: "Avustralya, yemek dağıtım kuryelerine yönelik yeni asgari ücret korumaları ve standartlar getiriyor. Bu reformlar, teslimat platformlarında ücret şeffaflığını ve çalışma koşullarını iyileştirmeyi amaçlıyor.",
    },
    typicalEarnings: {
      title: "Avustralya'da Tipik Teslimat Kazançları",
      ebike: { vehicle: "E-Bisiklet", range: "Saatte $18 – $28" },
      scooter: { vehicle: "Scooter / Motorsiklet", range: "Saatte $22 – $35" },
      car: { vehicle: "Araba", range: "Saatte $25 – $40" },
      note: "Kazançlar şehre, talebe, çalışılan saatlere ve kurye deneyimine göre değişir.",
    },
    ebikeRegulation: "Teslimat için kullanılan elektrikli bisikletler, Avustralya düzenlemelerine uymak için yalnızca pedal destekli (gazı olmayan) olmalıdır.",
    earningsCalculator: {
      title: "Teslimat Kazancınızı Tahmin Edin",
      city: "Şehir",
      cityPlaceholder: "Şehir seçin",
      cities: ["Sydney", "Melbourne", "Brisbane", "Diğer"],
      vehicle: "Araç Tipi",
      vehiclePlaceholder: "Araç seçin",
      vehicles: ["E-Bisiklet", "Scooter / Motorsiklet", "Araba"],
      hours: "Haftalık Saat",
      weeklyEarnings: "Tahmini haftalık kazanç",
      monthlyEarnings: "Tahmini aylık kazanç",
      disclaimer: "Tahminler Avustralya'daki tipik teslimat kazançlarına dayanmaktadır. Gerçek sonuçlar şehre, talebe, platforma ve kurye deneyimine göre değişebilir.",
    },
  },
  ru: {
    backToHome: "Вернуться на главную",
    completeGuide: "ПОЛНОЕ РУКОВОДСТВО 2026",
    heroTitle: "Как начать",
    heroTitleHighlight: "работу курьером",
    heroTitleEnd: "в Австралии",
    heroSubtitle: "Всё, что нужно знать, чтобы стать курьером Uber Eats, DoorDash и других платформ в Австралии.",
    viewRequirements: "Посмотреть требования",
    getHelpStarting: "Получить помощь",
    forInternational: "Для иностранных студентов и новых мигрантов",
    forInternationalDesc1: "Работа курьером в Австралии — популярный вариант для иностранных студентов и новых мигрантов, ищущих гибкий доход.",
    forInternationalDesc2: "Это руководство объясняет типичные требования, варианты транспорта и распространённые ошибки, которых следует избегать.",
    whatYouNeed: "Что вам понадобится",
    whatYouNeedSubtitle: "Типичные требования для регистрации на платформах доставки в Австралии",
    requirements: [
      { title: "Документы", desc: "Действующий паспорт или удостоверение личности" },
      { title: "Право на работу", desc: "Виза с правом работы в Австралии (проверка VEVO)" },
      { title: "Смартфон", desc: "Совместимый смартфон для использования приложения" },
      { title: "Банковский счёт", desc: "Австралийский банковский счёт для получения платежей" },
    ],
    policeCheck: "Проверка полиции",
    policeCheckDesc: "Некоторые платформы могут требовать проверку полиции при регистрации.",
    policeCheckExample: "Uber может взимать примерно",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Эта стоимость не включена в услуги Just Delivery.",
    licenceTitle: "Требования к водительским правам",
    licenceSubtitle: "Требования зависят от используемого транспорта",
    carDelivery: "Доставка на автомобиле",
    carDeliveryDesc: "Платформы вроде Uber Eats могут разрешить доставку с действующими иностранными правами.",
    carDeliveryNote: "Можно использовать иностранные права (при соблюдении требований)",
    scooterDelivery: "Доставка на скутере / мотоцикле",
    scooterDeliveryDesc: "Платформы обычно требуют действующие австралийские права для доставки на скутере или мотоцикле.",
    scooterOverseasNote: "Владельцы иностранных прав:",
    scooterOverseasDesc: "Доставка на скутере может потребовать конвертации иностранных прав.",
    scooterLicenceNote: "Может потребоваться конвертация прав",
    scooterLicenceLink: "Узнайте, как конвертировать права",
    ebikeDelivery: "Доставка на электровелосипеде / велосипеде",
    ebikeDeliveryDesc: "Обычно права не требуются для доставки на велосипеде или электровелосипеде.",
    ebikeLicenceNote: "Права не нужны для электровелосипедов до 250W",
    licenceDisclaimer: "Just Delivery предоставляет только консультации. Курьеры сами отвечают за соответствие требованиям.",
    vehicleOptions: "Варианты транспорта",
    vehicleOptionsSubtitle: "Выберите способ доставки, который подходит вам лучше всего",
    vehicles: {
      ebike: { title: "Электровелосипед", desc: "Часто самый простой способ начать.", pros: "Без прав, низкие затраты", cons: "Ограниченный запас хода, зависит от погоды", req: "Права не нужны" },
      car: { title: "Автомобиль", desc: "Хорош для дальних расстояний.", pros: "Больше комфорта, работа в любую погоду", cons: "Расходы на топливо, проблемы с парковкой", req: "Действующие права (иностранные могут подойти)" },
      scooter: { title: "Скутер", desc: "Быстро и эффективно для городских доставок.", pros: "Быстрее велосипедов", cons: "Может потребоваться конвертация прав", req: "Может потребоваться конвертация прав" },
    },
    licenceLabel: "Права:",
    onboardingProcess: "Процесс регистрации",
    onboardingSubtitle: "Пошагово от заявки до первой доставки",
    steps: [
      { title: "Подготовьте документы", desc: "Соберите документы, визу и банковские реквизиты." },
      { title: "Выберите платформу", desc: "Выберите Uber Eats, DoorDash или другую платформу." },
      { title: "Отправьте заявку", desc: "Заполните онлайн-заявку и загрузите документы." },
      { title: "Проверка данных", desc: "Некоторые платформы могут требовать проверку полиции." },
      { title: "Получите одобрение", desc: "После одобрения скачайте приложение и пройдите обучение." },
      { title: "Начните зарабатывать", desc: "Выходите онлайн и принимайте заказы на доставку!" },
    ],
    commonMistakes: "Распространённые ошибки",
    commonMistakesSubtitle: "Не позволяйте этим ошибкам задержать вашу заявку",
    mistakes: [
      { title: "Отправка неправильных документов", desc: "Использование просроченных документов может привести к отказу." },
      { title: "Непонимание требований к правам", desc: "Заявка на доставку скутером без австралийских прав будет отклонена." },
      { title: "Выбор неподходящего транспорта", desc: "Выбор транспорта без соответствующих прав задержит заявку." },
      { title: "Отсутствие права на работу", desc: "Попытка регистрации без права на работу приведёт к отказу." },
    ],
    howWeHelp: "Как Just Delivery помогает",
    howWeHelpDesc: "Just Delivery помогает курьерам подготовить документы и понять процесс.",
    helpFeatures: [
      { title: "Проверка документов", desc: "Мы проверяем ваши документы перед подачей" },
      { title: "Экспертное руководство", desc: "Пошаговая поддержка" },
      { title: "Экономия времени", desc: "Избегайте задержек" },
    ],
    startApplication: "Начать заявку",
    viewPackages: "Посмотреть пакеты",
    disclaimer: "Just Delivery не связан с Uber Eats, DoorDash или любой платформой доставки.",
    serviceDisclaimer: "Just Delivery — это независимая служба помощи при регистрации, не связанная с Uber Eats, DoorDash или какой-либо платформой доставки.",
    industryUpdate: {
      title: "Новости отрасли",
      text: "Австралия вводит новые минимальные защиты оплаты и стандарты для курьеров по доставке еды. Эти реформы направлены на повышение прозрачности оплаты и условий труда на платформах доставки.",
    },
    typicalEarnings: {
      title: "Типичный заработок курьера в Австралии",
      ebike: { vehicle: "Электровелосипед", range: "$18 – $28 в час" },
      scooter: { vehicle: "Скутер / Мотоцикл", range: "$22 – $35 в час" },
      car: { vehicle: "Автомобиль", range: "$25 – $40 в час" },
      note: "Заработок зависит от города, спроса, отработанных часов и опыта курьера.",
    },
    ebikeRegulation: "Электровелосипеды для доставки должны быть только с педальным приводом (без газа) для соответствия австралийским нормам.",
    earningsCalculator: {
      title: "Рассчитайте свой заработок курьером",
      city: "Город",
      cityPlaceholder: "Выберите город",
      cities: ["Сидней", "Мельбурн", "Брисбен", "Другой"],
      vehicle: "Тип транспорта",
      vehiclePlaceholder: "Выберите транспорт",
      vehicles: ["Электровелосипед", "Скутер / Мотоцикл", "Автомобиль"],
      hours: "Часов в неделю",
      weeklyEarnings: "Расчётный недельный заработок",
      monthlyEarnings: "Расчётный месячный заработок",
      disclaimer: "Расчёты основаны на типичном заработке курьеров в Австралии. Фактические результаты могут отличаться в зависимости от города, спроса, платформы и опыта курьера.",
    },
  },
  zh: {
    backToHome: "返回首页",
    completeGuide: "2026年完整指南",
    heroTitle: "如何开始",
    heroTitleHighlight: "配送工作",
    heroTitleEnd: "在澳大利亚",
    heroSubtitle: "成为Uber Eats、DoorDash和其他平台配送员所需了解的一切。",
    viewRequirements: "查看要求",
    getHelpStarting: "获取帮助",
    forInternational: "面向国际学生和新移民",
    forInternationalDesc1: "在澳大利亚开始配送工作是国际学生和新移民寻求灵活收入的热门选择。",
    forInternationalDesc2: "本指南解释了典型要求、车辆选择和需要避免的常见错误。",
    whatYouNeed: "您需要什么",
    whatYouNeedSubtitle: "在澳大利亚注册配送平台的典型要求",
    requirements: [
      { title: "身份证件", desc: "有效护照或身份证用于验证" },
      { title: "工作权", desc: "在澳大利亚具有工作权的签证（VEVO验证）" },
      { title: "智能手机", desc: "兼容的智能手机以使用配送应用" },
      { title: "银行账户", desc: "澳大利亚银行账户用于接收付款" },
    ],
    policeCheck: "警察检查",
    policeCheckDesc: "某些平台可能在注册时要求警察检查。",
    policeCheckExample: "Uber可能收取约",
    policeCheckFee: "$49澳元",
    policeCheckNote: "此费用不包含在Just Delivery服务中。",
    licenceTitle: "驾照要求",
    licenceSubtitle: "驾照要求取决于您使用的车辆",
    carDelivery: "汽车配送",
    carDeliveryDesc: "Uber Eats等平台可能允许使用有效的海外驾照进行配送。",
    carDeliveryNote: "可能使用海外驾照（视要求而定）",
    scooterDelivery: "踏板车/摩托车配送",
    scooterDeliveryDesc: "平台通常要求有效的澳大利亚驾照才能使用踏板车或摩托车配送。",
    scooterOverseasNote: "海外驾照持有者：",
    scooterOverseasDesc: "踏板车配送可能需要转换海外驾照。",
    scooterLicenceNote: "可能需要转换驾照",
    scooterLicenceLink: "查看如何转换驾照",
    ebikeDelivery: "电动自行车/自行车配送",
    ebikeDeliveryDesc: "自行车或电动自行车配送通常不需要驾照。",
    ebikeLicenceNote: "250W以下电动自行车无需驾照",
    licenceDisclaimer: "Just Delivery仅提供指导。骑手有责任确保满足要求。",
    vehicleOptions: "车辆选择",
    vehicleOptionsSubtitle: "选择最适合您的配送方式",
    vehicles: {
      ebike: { title: "电动自行车", desc: "通常是最容易开始的方式。", pros: "无需驾照，成本低", cons: "范围有限，受天气影响", req: "无需驾照" },
      car: { title: "汽车", desc: "适合长距离和大订单。", pros: "更舒适，任何天气都能工作", cons: "燃油成本，停车问题", req: "有效驾照（海外可能适用）" },
      scooter: { title: "踏板车", desc: "城市配送快速高效。", pros: "比自行车快", cons: "可能需要转换驾照", req: "可能需要转换驾照" },
    },
    licenceLabel: "驾照：",
    onboardingProcess: "注册流程",
    onboardingSubtitle: "从申请到首次配送的逐步指南",
    steps: [
      { title: "准备文件", desc: "收集您的身份证件、签证和银行信息。" },
      { title: "选择平台", desc: "选择Uber Eats、DoorDash或其他平台。" },
      { title: "提交申请", desc: "完成在线申请并上传文件。" },
      { title: "背景检查", desc: "某些平台可能需要警察检查。" },
      { title: "获得批准", desc: "批准后下载应用并完成培训。" },
      { title: "开始赚钱", desc: "准备好后上线并开始接受配送请求！" },
    ],
    commonMistakes: "需要避免的常见错误",
    commonMistakesSubtitle: "不要让这些错误延迟您的申请",
    mistakes: [
      { title: "提交错误文件", desc: "使用过期文件可能导致拒绝。" },
      { title: "误解驾照要求", desc: "没有澳大利亚驾照申请踏板车配送将被拒绝。" },
      { title: "选择错误的车辆", desc: "选择没有相应驾照的车辆会延迟申请。" },
      { title: "没有工作权", desc: "没有有效工作权的注册将被拒绝。" },
    ],
    howWeHelp: "Just Delivery如何帮助您",
    howWeHelpDesc: "Just Delivery帮助骑手准备文件并了解流程。",
    helpFeatures: [
      { title: "文件审查", desc: "我们在您申请前检查您的文件" },
      { title: "专家指导", desc: "逐步支持" },
      { title: "节省时间", desc: "避免延迟" },
    ],
    startApplication: "开始申请",
    viewPackages: "查看套餐",
    disclaimer: "Just Delivery与Uber Eats、DoorDash或任何配送平台无关。",
    serviceDisclaimer: "Just Delivery是一项独立的入职协助服务，与Uber Eats、DoorDash或任何配送平台无关。",
    industryUpdate: { title: "行业动态", text: "澳大利亚正在引入新的外卖配送员最低工资保护和标准。这些改革旨在提高配送平台的薪酬透明度和工作条件。" },
    typicalEarnings: { title: "澳大利亚典型配送收入", ebike: { vehicle: "电动自行车", range: "每小时 $18 – $28" }, scooter: { vehicle: "踏板车/摩托车", range: "每小时 $22 – $35" }, car: { vehicle: "汽车", range: "每小时 $25 – $40" }, note: "收入因城市、需求、工作时间和配送员经验而异。" },
    ebikeRegulation: "用于配送的电动自行车必须仅为助力型（无油门）以符合澳大利亚法规。",
    earningsCalculator: { title: "估算您的配送收入", city: "城市", cityPlaceholder: "选择城市", cities: ["悉尼", "墨尔本", "布里斯班", "其他"], vehicle: "车辆类型", vehiclePlaceholder: "选择车辆", vehicles: ["电动自行车", "踏板车/摩托车", "汽车"], hours: "每周工作时间", weeklyEarnings: "预估每周收入", monthlyEarnings: "预估每月收入", disclaimer: "估算基于澳大利亚典型配送收入。实际结果可能因城市、需求、平台和配送员经验而异。" },
  },
  ja: {
    backToHome: "ホームに戻る",
    completeGuide: "完全ガイド 2026",
    heroTitle: "始め方",
    heroTitleHighlight: "デリバリーの仕事",
    heroTitleEnd: "オーストラリアで",
    heroSubtitle: "Uber Eats、DoorDash、その他のプラットフォームでデリバリーライダーになるために知っておくべきすべて。",
    viewRequirements: "要件を見る",
    getHelpStarting: "ヘルプを得る",
    forInternational: "留学生と新移民の方へ",
    forInternationalDesc1: "オーストラリアでデリバリーの仕事を始めることは、柔軟な収入を求める留学生や新移民に人気の選択肢です。",
    forInternationalDesc2: "このガイドでは、典型的な要件、車両オプション、避けるべき一般的なミスについて説明します。",
    whatYouNeed: "必要なもの",
    whatYouNeedSubtitle: "オーストラリアでのデリバリープラットフォーム登録の典型的な要件",
    requirements: [
      { title: "身分証明書", desc: "確認用の有効なパスポートまたはID" },
      { title: "就労権", desc: "オーストラリアでの就労権を持つビザ（VEVO確認）" },
      { title: "スマートフォン", desc: "アプリを使用するための互換性のあるスマートフォン" },
      { title: "銀行口座", desc: "支払いを受け取るためのオーストラリアの銀行口座" },
    ],
    policeCheck: "警察チェック",
    policeCheckDesc: "一部のプラットフォームは登録時に警察チェックを要求する場合があります。",
    policeCheckExample: "Uberは約",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "この費用はJust Deliveryのサービスには含まれていません。",
    licenceTitle: "運転免許要件",
    licenceSubtitle: "免許要件は使用する車両によって異なります",
    carDelivery: "車での配達",
    carDeliveryDesc: "Uber Eatsなどのプラットフォームは、有効な海外免許での配達を許可する場合があります。",
    carDeliveryNote: "海外免許を使用できる可能性あり（要件による）",
    scooterDelivery: "スクーター/バイク配達",
    scooterDeliveryDesc: "プラットフォームは通常、スクーターやバイクでの配達に有効なオーストラリアの免許を要求します。",
    scooterOverseasNote: "海外免許保持者：",
    scooterOverseasDesc: "スクーター配達には海外免許の変換が必要な場合があります。",
    scooterLicenceNote: "免許の変換が必要な場合があります",
    scooterLicenceLink: "免許の変換方法を見る",
    ebikeDelivery: "電動自転車/自転車配達",
    ebikeDeliveryDesc: "自転車や電動自転車での配達には通常免許は必要ありません。",
    ebikeLicenceNote: "250W未満の電動自転車は免許不要",
    licenceDisclaimer: "Just Deliveryはガイダンスのみを提供します。ライダーは要件を満たす責任があります。",
    vehicleOptions: "車両オプション",
    vehicleOptionsSubtitle: "あなたに最適な配達方法を選択してください",
    vehicles: {
      ebike: { title: "電動自転車", desc: "始めるのに最も簡単な方法。", pros: "免許不要、低コスト", cons: "範囲限定、天候依存", req: "免許不要" },
      car: { title: "車", desc: "長距離や大きな注文に適しています。", pros: "より快適、どんな天候でも作業可能", cons: "燃料費、駐車の問題", req: "有効な免許（海外も可能性あり）" },
      scooter: { title: "スクーター", desc: "都市での配達に速くて効率的。", pros: "自転車より速い", cons: "免許の変換が必要な場合があります", req: "免許の変換が必要な場合があります" },
    },
    licenceLabel: "免許：",
    onboardingProcess: "登録プロセス",
    onboardingSubtitle: "申請から最初の配達までステップバイステップ",
    steps: [
      { title: "書類を準備", desc: "ID、ビザ書類、銀行情報を集めてください。" },
      { title: "プラットフォームを選択", desc: "Uber Eats、DoorDash、または他のプラットフォームを選択。" },
      { title: "申請を送信", desc: "オンライン申請を完了し、書類をアップロード。" },
      { title: "バックグラウンドチェック", desc: "一部のプラットフォームは警察チェックを要求する場合があります。" },
      { title: "承認を得る", desc: "承認後、アプリをダウンロードしてトレーニングを完了。" },
      { title: "稼ぎ始める", desc: "準備ができたらオンラインにして配達リクエストを受け付け開始！" },
    ],
    commonMistakes: "避けるべき一般的なミス",
    commonMistakesSubtitle: "これらのエラーで申請を遅らせないでください",
    mistakes: [
      { title: "間違った書類の提出", desc: "期限切れの書類を使用すると却下される可能性があります。" },
      { title: "免許要件の誤解", desc: "オーストラリアの免許なしでスクーター配達を申請すると却下されます。" },
      { title: "間違った車両オプションの選択", desc: "適切な免許のない車両を選択すると申請が遅れます。" },
      { title: "就労権がない", desc: "有効な就労権なしで登録しようとすると却下されます。" },
    ],
    howWeHelp: "Just Deliveryがどのように役立つか",
    howWeHelpDesc: "Just Deliveryはライダーの書類準備とプロセス理解を支援します。",
    helpFeatures: [
      { title: "書類レビュー", desc: "申請前に書類をチェックします" },
      { title: "専門家のガイダンス", desc: "ステップバイステップのサポート" },
      { title: "時間を節約", desc: "遅延を避ける" },
    ],
    startApplication: "申請を開始",
    viewPackages: "パッケージを見る",
    disclaimer: "Just DeliveryはUber Eats、DoorDash、またはいかなるデリバリープラットフォームとも提携していません。",
    serviceDisclaimer: "Just Deliveryは独立したオンボーディング支援サービスであり、Uber Eats、DoorDash、またはいかなるデリバリープラットフォームとも提携していません。",
    industryUpdate: { title: "業界ニュース", text: "オーストラリアはフードデリバリーライダー向けの新しい最低賃金保護と基準を導入しています。これらの改革は、デリバリープラットフォーム全体での賃金の透明性と労働条件の改善を目指しています。" },
    typicalEarnings: { title: "オーストラリアでの一般的なデリバリー収入", ebike: { vehicle: "電動アシスト自転車", range: "時給 $18 – $28" }, scooter: { vehicle: "スクーター/バイク", range: "時給 $22 – $35" }, car: { vehicle: "車", range: "時給 $25 – $40" }, note: "収入は都市、需要、勤務時間、ライダーの経験によって異なります。" },
    ebikeRegulation: "デリバリーに使用する電動自転車は、オーストラリアの規制に準拠するために、ペダルアシストのみ（スロットルなし）である必要があります。",
    earningsCalculator: { title: "デリバリー収入を見積もる", city: "都市", cityPlaceholder: "都市を選択", cities: ["シドニー", "メルボルン", "ブリスベン", "その他"], vehicle: "車両タイプ", vehiclePlaceholder: "車両を選択", vehicles: ["電動アシスト自転車", "スクーター/バイク", "車"], hours: "週あたりの時間", weeklyEarnings: "推定週間収入", monthlyEarnings: "推定月間収入", disclaimer: "見積もりはオーストラリアでの一般的なデリバリー収入に基づいています。実際の結果は、都市、需要、プラットフォーム、ライダーの経験によって異なる場合があります。" },
  },
  // Portuguese - Português
  pt: {
    backToHome: "Voltar ao início",
    completeGuide: "Guia Completo 2026",
    heroTitle: "Como Começar",
    heroTitleHighlight: "Trabalho de Entregas",
    heroTitleEnd: "na Austrália",
    heroSubtitle: "Tudo o que precisa saber para se tornar um entregador no Uber Eats, DoorDash e outras plataformas.",
    viewRequirements: "Ver Requisitos",
    getHelpStarting: "Obter Ajuda",
    forInternational: "Para Estudantes Internacionais e Recém-Chegados",
    forInternationalDesc1: "Começar trabalho de entregas na Austrália é uma escolha popular para estudantes internacionais e recém-chegados que procuram rendimento flexível.",
    forInternationalDesc2: "Este guia cobre os requisitos típicos, opções de veículos e erros comuns a evitar.",
    whatYouNeed: "O Que Precisa",
    whatYouNeedSubtitle: "Requisitos típicos para registo em plataformas de entregas na Austrália",
    requirements: [
      { title: "Documento de Identidade", desc: "Passaporte válido ou ID para verificação" },
      { title: "Direito ao Trabalho", desc: "Visto com direitos de trabalho na Austrália (verificação VEVO)" },
      { title: "Smartphone", desc: "Smartphone compatível para usar a app" },
      { title: "Conta Bancária", desc: "Conta bancária australiana para receber pagamentos" },
    ],
    policeCheck: "Verificação Policial",
    policeCheckDesc: "Algumas plataformas podem requerer verificação policial durante o registo.",
    policeCheckExample: "Uber pode cobrar aproximadamente",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "Esta taxa não está incluída no serviço Just Delivery.",
    licenceTitle: "Requisitos de Carta de Condução",
    licenceSubtitle: "Os requisitos de carta variam consoante o veículo que utiliza",
    carDelivery: "Entrega de Carro",
    carDeliveryDesc: "Plataformas como Uber Eats podem permitir entregas com carta estrangeira válida.",
    carDeliveryNote: "Pode ser possível usar carta estrangeira (sujeito a requisitos)",
    scooterDelivery: "Entrega de Scooter/Mota",
    scooterDeliveryDesc: "As plataformas geralmente requerem carta australiana válida para entregas de scooter ou mota.",
    scooterOverseasNote: "Titulares de carta estrangeira:",
    scooterOverseasDesc: "Pode ser necessária conversão da carta estrangeira para entregas de scooter.",
    scooterLicenceNote: "Pode ser necessária conversão de carta",
    scooterLicenceLink: "Ver como converter a carta",
    ebikeDelivery: "Entrega de Bicicleta Elétrica/Bicicleta",
    ebikeDeliveryDesc: "Entregas de bicicleta ou e-bike geralmente não requerem carta.",
    ebikeLicenceNote: "E-bikes até 250W não requerem carta",
    licenceDisclaimer: "Just Delivery fornece apenas orientação. Os entregadores são responsáveis por cumprir os requisitos.",
    vehicleOptions: "Opções de Veículos",
    vehicleOptionsSubtitle: "Escolha o melhor método de entrega para si",
    vehicles: {
      ebike: { title: "Bicicleta Elétrica", desc: "A forma mais fácil de começar.", pros: "Sem carta necessária, baixo custo", cons: "Alcance limitado, dependente do clima", req: "Sem carta necessária" },
      car: { title: "Carro", desc: "Bom para distâncias maiores e encomendas grandes.", pros: "Mais confortável, trabalha em qualquer clima", cons: "Custos de combustível, problemas de estacionamento", req: "Carta válida (estrangeira pode ser possível)" },
      scooter: { title: "Scooter", desc: "Rápida e eficiente para entregas urbanas.", pros: "Mais rápida que bicicleta", cons: "Pode requerer conversão de carta", req: "Pode requerer conversão de carta" },
    },
    licenceLabel: "Carta:",
    onboardingProcess: "Processo de Registo",
    onboardingSubtitle: "Passo a passo desde a candidatura até à primeira entrega",
    steps: [
      { title: "Prepare os Documentos", desc: "Reúna o seu ID, documentos de visto e informações bancárias." },
      { title: "Escolha a Plataforma", desc: "Selecione Uber Eats, DoorDash ou outra plataforma." },
      { title: "Submeta a Candidatura", desc: "Complete a candidatura online e carregue os documentos." },
      { title: "Verificação de Antecedentes", desc: "Algumas plataformas podem requerer verificação policial." },
      { title: "Seja Aprovado", desc: "Após aprovação, descarregue a app e complete o treino." },
      { title: "Comece a Ganhar", desc: "Fique online quando pronto e comece a aceitar pedidos de entrega!" },
    ],
    commonMistakes: "Erros Comuns a Evitar",
    commonMistakesSubtitle: "Não deixe estes erros atrasar a sua candidatura",
    mistakes: [
      { title: "Submeter Documentos Errados", desc: "Usar documentos expirados pode resultar em rejeição." },
      { title: "Não Entender Requisitos de Carta", desc: "Candidatar-se para entrega de scooter sem carta australiana será rejeitado." },
      { title: "Escolher Opção de Veículo Errada", desc: "Selecionar um veículo sem a carta adequada atrasa a candidatura." },
      { title: "Não Ter Direito ao Trabalho", desc: "Tentar registar sem direitos de trabalho válidos resulta em rejeição." },
    ],
    howWeHelp: "Como Just Delivery Ajuda",
    howWeHelpDesc: "Just Delivery ajuda os entregadores a preparar documentos e entender o processo.",
    helpFeatures: [
      { title: "Revisão de Documentos", desc: "Verificamos os seus documentos antes da submissão" },
      { title: "Orientação Especializada", desc: "Suporte passo a passo" },
      { title: "Poupe Tempo", desc: "Evite atrasos" },
    ],
    startApplication: "Iniciar Candidatura",
    viewPackages: "Ver Pacotes",
    disclaimer: "Just Delivery não é afiliado ao Uber Eats, DoorDash ou qualquer plataforma de entregas.",
    serviceDisclaimer: "Just Delivery é um serviço independente de assistência ao registo e não é afiliado ao Uber Eats, DoorDash ou qualquer plataforma de entregas.",
    industryUpdate: { title: "Atualização do Setor", text: "A Austrália está a introduzir novas proteções salariais mínimas e padrões para entregadores de comida. Estas reformas visam melhorar a transparência salarial e as condições de trabalho nas plataformas de entregas." },
    typicalEarnings: { title: "Ganhos Típicos de Entregas na Austrália", ebike: { vehicle: "E-Bike", range: "$18 – $28 por hora" }, scooter: { vehicle: "Scooter / Mota", range: "$22 – $35 por hora" }, car: { vehicle: "Carro", range: "$25 – $40 por hora" }, note: "Os ganhos variam consoante a cidade, procura, horas trabalhadas e experiência do entregador." },
    ebikeRegulation: "As bicicletas elétricas usadas para entregas devem ser apenas de assistência ao pedal (sem acelerador) para cumprir os regulamentos australianos.",
    earningsCalculator: { title: "Estime os Seus Ganhos de Entregas", city: "Cidade", cityPlaceholder: "Selecione a cidade", cities: ["Sydney", "Melbourne", "Brisbane", "Outro"], vehicle: "Tipo de Veículo", vehiclePlaceholder: "Selecione o veículo", vehicles: ["E-Bike", "Scooter / Mota", "Carro"], hours: "Horas por Semana", weeklyEarnings: "Ganhos semanais estimados", monthlyEarnings: "Ganhos mensais estimados", disclaimer: "As estimativas são baseadas nos ganhos típicos de entregas na Austrália. Os resultados reais podem variar consoante a cidade, procura, plataforma e experiência do entregador." },
  },
  // Korean - 한국어
  ko: {
    backToHome: "홈으로 돌아가기",
    completeGuide: "완전 가이드 2026",
    heroTitle: "시작하는 방법",
    heroTitleHighlight: "배달 일",
    heroTitleEnd: "호주에서",
    heroSubtitle: "Uber Eats, DoorDash 및 기타 플랫폼에서 배달 라이더가 되기 위해 알아야 할 모든 것.",
    viewRequirements: "요건 보기",
    getHelpStarting: "도움 받기",
    forInternational: "유학생 및 신규 이민자를 위해",
    forInternationalDesc1: "호주에서 배달 일을 시작하는 것은 유연한 수입을 원하는 유학생과 신규 이민자에게 인기 있는 선택입니다.",
    forInternationalDesc2: "이 가이드는 일반적인 요건, 차량 옵션 및 피해야 할 일반적인 실수를 다룹니다.",
    whatYouNeed: "필요한 것",
    whatYouNeedSubtitle: "호주 배달 플랫폼 등록을 위한 일반적인 요건",
    requirements: [
      { title: "신분 증명서", desc: "확인을 위한 유효한 여권 또는 ID" },
      { title: "취업 권리", desc: "호주에서 일할 수 있는 비자 (VEVO 확인)" },
      { title: "스마트폰", desc: "앱을 사용할 수 있는 호환 스마트폰" },
      { title: "은행 계좌", desc: "급여를 받을 호주 은행 계좌" },
    ],
    policeCheck: "경찰 신원조회",
    policeCheckDesc: "일부 플랫폼은 등록 시 경찰 신원조회를 요구할 수 있습니다.",
    policeCheckExample: "Uber는 약",
    policeCheckFee: "$49 AUD",
    policeCheckNote: "이 비용은 Just Delivery 서비스에 포함되지 않습니다.",
    licenceTitle: "운전면허 요건",
    licenceSubtitle: "면허 요건은 사용하는 차량에 따라 다릅니다",
    carDelivery: "자동차 배달",
    carDeliveryDesc: "Uber Eats와 같은 플랫폼은 유효한 외국 면허로 배달을 허용할 수 있습니다.",
    carDeliveryNote: "외국 면허 사용 가능 (요건에 따라)",
    scooterDelivery: "스쿠터/오토바이 배달",
    scooterDeliveryDesc: "플랫폼은 일반적으로 스쿠터나 오토바이 배달에 유효한 호주 면허를 요구합니다.",
    scooterOverseasNote: "외국 면허 소지자:",
    scooterOverseasDesc: "스쿠터 배달을 위해 외국 면허 전환이 필요할 수 있습니다.",
    scooterLicenceNote: "면허 전환이 필요할 수 있음",
    scooterLicenceLink: "면허 전환 방법 보기",
    ebikeDelivery: "전기자전거/자전거 배달",
    ebikeDeliveryDesc: "자전거나 전기자전거 배달은 일반적으로 면허가 필요하지 않습니다.",
    ebikeLicenceNote: "250W 이하 전기자전거는 면허 불필요",
    licenceDisclaimer: "Just Delivery는 안내만 제공합니다. 라이더는 요건 충족에 대한 책임이 있습니다.",
    vehicleOptions: "차량 옵션",
    vehicleOptionsSubtitle: "자신에게 가장 적합한 배달 방법을 선택하세요",
    vehicles: {
      ebike: { title: "전기자전거", desc: "가장 쉽게 시작하는 방법.", pros: "면허 불필요, 저비용", cons: "제한된 범위, 날씨 의존", req: "면허 불필요" },
      car: { title: "자동차", desc: "장거리 및 대형 주문에 적합.", pros: "더 편안함, 모든 날씨에서 작업 가능", cons: "연료비, 주차 문제", req: "유효한 면허 (외국 면허 가능)" },
      scooter: { title: "스쿠터", desc: "도시 배달에 빠르고 효율적.", pros: "자전거보다 빠름", cons: "면허 전환 필요할 수 있음", req: "면허 전환 필요할 수 있음" },
    },
    licenceLabel: "면허:",
    onboardingProcess: "등록 프로세스",
    onboardingSubtitle: "신청부터 첫 배달까지 단계별 안내",
    steps: [
      { title: "서류 준비", desc: "ID, 비자 서류 및 은행 정보를 모으세요." },
      { title: "플랫폼 선택", desc: "Uber Eats, DoorDash 또는 다른 플랫폼 선택." },
      { title: "신청서 제출", desc: "온라인 신청서를 작성하고 서류를 업로드." },
      { title: "배경 조사", desc: "일부 플랫폼은 경찰 신원조회를 요구할 수 있습니다." },
      { title: "승인 받기", desc: "승인 후 앱을 다운로드하고 교육을 완료." },
      { title: "수익 시작", desc: "준비되면 온라인으로 전환하고 배달 요청을 받기 시작!" },
    ],
    commonMistakes: "피해야 할 일반적인 실수",
    commonMistakesSubtitle: "이러한 오류로 신청이 지연되지 않도록 하세요",
    mistakes: [
      { title: "잘못된 서류 제출", desc: "만료된 서류를 사용하면 거부될 수 있습니다." },
      { title: "면허 요건 오해", desc: "호주 면허 없이 스쿠터 배달을 신청하면 거부됩니다." },
      { title: "잘못된 차량 옵션 선택", desc: "적절한 면허 없이 차량을 선택하면 신청이 지연됩니다." },
      { title: "취업 권리 없음", desc: "유효한 취업 권리 없이 등록을 시도하면 거부됩니다." },
    ],
    howWeHelp: "Just Delivery가 어떻게 도와주는지",
    howWeHelpDesc: "Just Delivery는 라이더가 서류를 준비하고 프로세스를 이해하도록 도와줍니다.",
    helpFeatures: [
      { title: "서류 검토", desc: "제출 전 서류를 확인합니다" },
      { title: "전문가 안내", desc: "단계별 지원" },
      { title: "시간 절약", desc: "지연 방지" },
    ],
    startApplication: "신청 시작",
    viewPackages: "패키지 보기",
    disclaimer: "Just Delivery는 Uber Eats, DoorDash 또는 어떤 배달 플랫폼과도 제휴하지 않습니다.",
    serviceDisclaimer: "Just Delivery는 독립적인 온보딩 지원 서비스이며 Uber Eats, DoorDash 또는 어떤 배달 플랫폼과도 제휴하지 않습니다.",
    industryUpdate: { title: "업계 소식", text: "호주는 음식 배달 라이더를 위한 새로운 최저 임금 보호 및 기준을 도입하고 있습니다. 이러한 개혁은 배달 플랫폼 전반의 급여 투명성과 근무 조건을 개선하는 것을 목표로 합니다." },
    typicalEarnings: { title: "호주의 일반적인 배달 수입", ebike: { vehicle: "전기자전거", range: "시간당 $18 – $28" }, scooter: { vehicle: "스쿠터 / 오토바이", range: "시간당 $22 – $35" }, car: { vehicle: "자동차", range: "시간당 $25 – $40" }, note: "수입은 도시, 수요, 근무 시간 및 라이더 경험에 따라 다릅니다." },
    ebikeRegulation: "배달에 사용되는 전기자전거는 호주 규정을 준수하기 위해 페달 어시스트만 가능해야 합니다(스로틀 없음).",
    earningsCalculator: { title: "배달 수입 예상하기", city: "도시", cityPlaceholder: "도시 선택", cities: ["시드니", "멜버른", "브리즈번", "기타"], vehicle: "차량 유형", vehiclePlaceholder: "차량 선택", vehicles: ["전기자전거", "스쿠터 / 오토바이", "자동차"], hours: "주당 시간", weeklyEarnings: "예상 주간 수입", monthlyEarnings: "예상 월간 수입", disclaimer: "예상치는 호주의 일반적인 배달 수입을 기준으로 합니다. 실제 결과는 도시, 수요, 플랫폼 및 라이더 경험에 따라 다를 수 있습니다." },
  },
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Expandable Section Component
const ExpandableSection = ({ title, children, icon: Icon, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="border border-gray-800 rounded-xl overflow-hidden bg-[#111111]"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-900/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-[#6ABF4B]" />}
          <span className="font-semibold text-white">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-6 pb-6 pt-2">
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Step Card Component
const StepCard = ({ number, title, description, icon: Icon, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.5 }}
    className="flex gap-4 items-start"
  >
    <div className="flex-shrink-0 w-12 h-12 bg-[#6ABF4B] rounded-full flex items-center justify-center">
      <span className="text-white font-bold text-lg">{number}</span>
    </div>
    <div className="flex-1">
      <h4 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5 text-[#6ABF4B]" />}
        {title}
      </h4>
      <p className="text-gray-400">{description}</p>
    </div>
  </motion.div>
);

// Vehicle Option Card
const VehicleCard = ({ title, description, icon: Icon, pros, cons, requirement, licenceLabel, extraNote }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-[#111111] border border-gray-800 rounded-xl p-6 hover:border-[#6ABF4B]/50 transition-colors"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center">
        <Icon className="w-6 h-6 text-[#6ABF4B]" />
      </div>
      <h3 className="text-xl font-bold text-white">{title}</h3>
    </div>
    <p className="text-gray-400 mb-4">{description}</p>
    
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
        <span className="text-gray-300 text-sm">{pros}</span>
      </div>
      {cons && (
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
          <span className="text-gray-300 text-sm">{cons}</span>
        </div>
      )}
      {extraNote && (
        <div className="flex items-start gap-2 pt-2 border-t border-gray-800/50">
          <AlertCircle className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
          <span className="text-blue-300 text-xs italic">{extraNote}</span>
        </div>
      )}
      <div className="pt-3 border-t border-gray-800">
        <span className="text-xs text-[#6ABF4B] font-medium">{licenceLabel} </span>
        <span className="text-xs text-gray-400">{requirement}</span>
      </div>
    </div>
  </motion.div>
);

// Mistake Card
const MistakeCard = ({ title, description }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    className="bg-red-500/10 border border-red-500/30 rounded-lg p-4"
  >
    <div className="flex items-start gap-3">
      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div>
        <h4 className="font-semibold text-red-400 mb-1">{title}</h4>
        <p className="text-gray-400 text-sm">{description}</p>
      </div>
    </div>
  </motion.div>
);

export default function DeliveryGuide() {
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') || 'en';
  const t = content[lang] || content.en;
  
  // Earnings Calculator State
  const [calcCity, setCalcCity] = useState('Sydney');
  const [calcVehicle, setCalcVehicle] = useState('E-Bike');
  const [calcHours, setCalcHours] = useState(30);

  // Set page title for SEO
  useEffect(() => {
    document.title = 'How to Start Delivery Work in Australia (Uber Eats & DoorDash Guide)';
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Complete guide on how to start delivery driver work in Australia. Learn Uber Eats requirements, DoorDash registration, bike delivery options, and driver licence requirements for food delivery riders.');
    }
    
    return () => {
      document.title = 'Just Delivery';
    };
  }, []);

  const stepIcons = [FileText, Smartphone, Users, Shield, CheckCircle2, DollarSign];

  return (
    <div className="min-h-screen bg-black text-white">
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#6ABF4B]/10 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Link to={`/?lang=${lang}`} className="inline-flex items-center gap-2 text-[#6ABF4B] hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" /> {t.backToHome}
          </Link>
          
          <motion.div initial="initial" animate="animate" variants={stagger}>
            <motion.p variants={fadeInUp} className="text-[#6ABF4B] font-medium mb-4">
              {t.completeGuide}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {t.heroTitle} <span className="text-[#6ABF4B]">{t.heroTitleHighlight}</span> {t.heroTitleEnd}
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-gray-400 mb-8 max-w-2xl">
              {t.heroSubtitle}
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a href="#requirements" className="px-6 py-3 bg-[#6ABF4B] text-white font-semibold rounded-lg hover:bg-[#5aa93f] transition-colors flex items-center gap-2">
                {t.viewRequirements} <ArrowRight className="w-4 h-4" />
              </a>
              <Link to={`/?lang=${lang}#contact`} className="px-6 py-3 border border-gray-700 text-white font-semibold rounded-lg hover:border-[#6ABF4B] transition-colors">
                {t.getHelpStarting}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#111111] border border-gray-800 rounded-2xl p-8"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-[#6ABF4B]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t.forInternational}</h2>
                <p className="text-gray-400 mb-4">{t.forInternationalDesc1}</p>
                <p className="text-gray-400">{t.forInternationalDesc2}</p>
              </div>
            </div>
          </motion.div>
          
          {/* Industry Update */}
          {t.industryUpdate && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-6 bg-[#6ABF4B]/10 border border-[#6ABF4B]/30 rounded-xl p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-[#6ABF4B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#6ABF4B] mb-2">{t.industryUpdate.title}</h3>
                  <p className="text-gray-300 text-sm">{t.industryUpdate.text}</p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Requirements Section */}
      <section id="requirements" className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.whatYouNeed}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t.whatYouNeedSubtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {[FileText, Shield, Smartphone, CreditCard].map((Icon, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#111111] border border-gray-800 rounded-xl p-6 flex items-start gap-4"
              >
                <div className="w-10 h-10 bg-[#6ABF4B]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#6ABF4B]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">{t.requirements[i].title}</h3>
                  <p className="text-gray-400 text-sm">{t.requirements[i].desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Police Check Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-xl font-bold text-yellow-400 mb-3">{t.policeCheck}</h3>
                <p className="text-gray-300 mb-3">{t.policeCheckDesc}</p>
                <div className="bg-black/30 rounded-lg p-4 mb-3">
                  <p className="text-gray-300">
                    <strong className="text-white">Example:</strong> {t.policeCheckExample} <strong className="text-yellow-400">{t.policeCheckFee}</strong>
                  </p>
                </div>
                <p className="text-gray-400 text-sm">{t.policeCheckNote}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Driver Licence Requirements */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.licenceTitle}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t.licenceSubtitle}</p>
          </motion.div>

          <div className="space-y-4">
            <ExpandableSection title={t.carDelivery} icon={Car} defaultOpen={true}>
              <p className="text-gray-400 mb-3">{t.carDeliveryDesc}</p>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-400 text-sm flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t.carDeliveryNote}</span>
                </p>
              </div>
            </ExpandableSection>

            <ExpandableSection title={t.scooterDelivery} icon={Bike}>
              <p className="text-gray-400 mb-3">{t.scooterDeliveryDesc}</p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3">
                <p className="text-yellow-400 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>{t.scooterOverseasNote}</strong> {t.scooterOverseasDesc}</span>
                </p>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-3">
                <p className="text-yellow-400 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t.scooterLicenceNote}</span>
                </p>
              </div>
              <Link to={`/?lang=${lang}#required-documents`} className="inline-flex items-center gap-2 text-[#6ABF4B] text-sm hover:underline">
                <ArrowRight className="w-4 h-4" />
                {t.scooterLicenceLink || "See how to convert your licence"}
              </Link>
            </ExpandableSection>

            <ExpandableSection title={t.ebikeDelivery} icon={Bike}>
              <p className="text-gray-400 mb-3">{t.ebikeDeliveryDesc}</p>
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-400 text-sm flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t.ebikeLicenceNote}</span>
                </p>
              </div>
            </ExpandableSection>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm mt-6 text-center"
          >
            {t.licenceDisclaimer}
          </motion.p>
        </div>
      </section>

      {/* Vehicle Options */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.vehicleOptions}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t.vehicleOptionsSubtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <VehicleCard
              title={t.vehicles.ebike.title}
              description={t.vehicles.ebike.desc}
              icon={Bike}
              pros={t.vehicles.ebike.pros}
              cons={t.vehicles.ebike.cons}
              requirement={t.vehicles.ebike.req}
              licenceLabel={t.licenceLabel}
              extraNote={t.ebikeRegulation}
            />
            <VehicleCard
              title={t.vehicles.car.title}
              description={t.vehicles.car.desc}
              icon={Car}
              pros={t.vehicles.car.pros}
              cons={t.vehicles.car.cons}
              requirement={t.vehicles.car.req}
              licenceLabel={t.licenceLabel}
            />
            <VehicleCard
              title={t.vehicles.scooter.title}
              description={t.vehicles.scooter.desc}
              icon={Bike}
              pros={t.vehicles.scooter.pros}
              cons={t.vehicles.scooter.cons}
              requirement={t.vehicles.scooter.req}
              licenceLabel={t.licenceLabel}
            />
          </div>
        </div>
      </section>

      {/* Typical Earnings Section */}
      {t.typicalEarnings && (
        <section className="py-16 bg-[#0a0a0a]">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.typicalEarnings.title}</h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[t.typicalEarnings.ebike, t.typicalEarnings.scooter, t.typicalEarnings.car].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-[#111111] border border-gray-800 rounded-xl p-6 text-center"
                >
                  <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    {i === 0 ? <Bike className="w-6 h-6 text-[#6ABF4B]" /> : i === 2 ? <Car className="w-6 h-6 text-[#6ABF4B]" /> : <Bike className="w-6 h-6 text-[#6ABF4B]" />}
                  </div>
                  <h3 className="font-semibold text-white mb-2">{item.vehicle}</h3>
                  <p className="text-[#6ABF4B] text-xl font-bold">{item.range}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-gray-500 text-sm text-center"
            >
              {t.typicalEarnings.note}
            </motion.p>
          </div>
        </section>
      )}

      {/* Earnings Calculator */}
      {t.earningsCalculator && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.earningsCalculator.title}</h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#111111] border border-gray-800 rounded-2xl p-8"
            >
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* City Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">{t.earningsCalculator.city}</label>
                  <select
                    value={calcCity}
                    onChange={(e) => setCalcCity(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none"
                  >
                    {(t.earningsCalculator.cities || ['Sydney', 'Melbourne', 'Brisbane', 'Other']).map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">{t.earningsCalculator.vehicle}</label>
                  <select
                    value={calcVehicle}
                    onChange={(e) => setCalcVehicle(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none"
                  >
                    {(t.earningsCalculator.vehicles || ['E-Bike', 'Scooter / Motorbike', 'Car']).map((vehicle) => (
                      <option key={vehicle} value={vehicle}>{vehicle}</option>
                    ))}
                  </select>
                </div>

                {/* Hours per Week */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">{t.earningsCalculator.hours}: {calcHours}h</label>
                  <input
                    type="range"
                    min="10"
                    max="60"
                    value={calcHours}
                    onChange={(e) => setCalcHours(Number(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#6ABF4B]"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10h</span>
                    <span>60h</span>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-[#6ABF4B]/10 border border-[#6ABF4B]/30 rounded-xl p-6 text-center">
                  <p className="text-gray-400 text-sm mb-2">{t.earningsCalculator.weeklyEarnings}</p>
                  <p className="text-[#6ABF4B] text-2xl font-bold">
                    ${Math.round(calcHours * (calcVehicle.includes('E-Bike') || calcVehicle.includes('Vélo') || calcVehicle.includes('전기') ? 20 : calcVehicle.includes('Car') || calcVehicle.includes('Auto') || calcVehicle.includes('Voiture') || calcVehicle.includes('자동차') ? 26 : 24))} – ${Math.round(calcHours * (calcVehicle.includes('E-Bike') || calcVehicle.includes('Vélo') || calcVehicle.includes('전기') ? 28 : calcVehicle.includes('Car') || calcVehicle.includes('Auto') || calcVehicle.includes('Voiture') || calcVehicle.includes('자동차') ? 38 : 34))}
                  </p>
                </div>
                <div className="bg-[#6ABF4B]/10 border border-[#6ABF4B]/30 rounded-xl p-6 text-center">
                  <p className="text-gray-400 text-sm mb-2">{t.earningsCalculator.monthlyEarnings}</p>
                  <p className="text-[#6ABF4B] text-2xl font-bold">
                    ${Math.round(calcHours * (calcVehicle.includes('E-Bike') || calcVehicle.includes('Vélo') || calcVehicle.includes('전기') ? 20 : calcVehicle.includes('Car') || calcVehicle.includes('Auto') || calcVehicle.includes('Voiture') || calcVehicle.includes('자동차') ? 26 : 24) * 4)} – ${Math.round(calcHours * (calcVehicle.includes('E-Bike') || calcVehicle.includes('Vélo') || calcVehicle.includes('전기') ? 28 : calcVehicle.includes('Car') || calcVehicle.includes('Auto') || calcVehicle.includes('Voiture') || calcVehicle.includes('자동차') ? 38 : 34) * 4)}
                  </p>
                </div>
              </div>

              <p className="text-gray-500 text-xs text-center">
                {t.earningsCalculator.disclaimer}
              </p>
            </motion.div>
          </div>
        </section>
      )}

      {/* Onboarding Process */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.onboardingProcess}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t.onboardingSubtitle}</p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#6ABF4B] to-transparent hidden md:block" />
            <div className="space-y-8">
              {t.steps.map((step, i) => (
                <StepCard
                  key={i}
                  number={i + 1}
                  title={step.title}
                  description={step.desc}
                  icon={stepIcons[i]}
                  delay={i * 0.1}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.commonMistakes}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">{t.commonMistakesSubtitle}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {t.mistakes.map((mistake, i) => (
              <MistakeCard key={i} title={mistake.title} description={mistake.desc} />
            ))}
          </div>
        </div>
      </section>

      {/* How Just Delivery Helps */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#6ABF4B]/20 to-transparent border border-[#6ABF4B]/30 rounded-2xl p-8 md:p-12 text-center"
          >
            <Zap className="w-12 h-12 text-[#6ABF4B] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.howWeHelp}</h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">{t.howWeHelpDesc}</p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[FileText, HelpCircle, Clock].map((Icon, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-[#6ABF4B]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-[#6ABF4B]" />
                  </div>
                  <h4 className="font-semibold text-white mb-1">{t.helpFeatures[i].title}</h4>
                  <p className="text-gray-400 text-sm">{t.helpFeatures[i].desc}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={`/?lang=${lang}#contact`} className="px-8 py-4 bg-[#6ABF4B] text-white font-bold rounded-lg hover:bg-[#5aa93f] transition-colors inline-flex items-center justify-center gap-2">
                {t.startApplication} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={`/?lang=${lang}#packages`} className="px-8 py-4 border border-gray-700 text-white font-semibold rounded-lg hover:border-[#6ABF4B] transition-colors">
                {t.viewPackages}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">{t.disclaimer}</p>
          {t.serviceDisclaimer && (
            <p className="text-gray-600 text-xs mt-3 italic">{t.serviceDisclaimer}</p>
          )}
          <p className="text-gray-600 text-xs mt-2">
            © {new Date().getFullYear()} JD NETWORK PTY LTD (ACN 695 764 071)
          </p>
        </div>
      </section>
    </div>
  );
}
